import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import AIPremiumButton from '../../components/AIPremiumGate';
import ManualTravelDetailsDialog from '../../components/ManualTravelDetailsDialog';
import CollapsibleItinerarySelector from '../../components/CollapsibleItinerarySelector';
import WhatsAppSendConfirmationDialog from '../../components/WhatsAppSendConfirmationDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../components/ui/alert';
import { 
  Plus, 
  MagnifyingGlass, 
  DotsThree,
  Upload,
  DownloadSimple,
  UserCircle,
  Check,
  X,
  Clock,
  Shield,
  PencilSimple,
  Trash,
  WhatsappLogo,
  PaperPlaneTilt,
  Warning,
  CheckCircle,
  Eye,
  Envelope,
  SpinnerGap,
  Palette,
  Phone,
  AirplaneTakeoff,
  AirplaneLanding,
  IdentificationCard,
  UsersThree,
  Heart,
  Minus,
  FunnelSimple,
  SlidersHorizontal,
  ArrowRight,
  Sparkle,
  AddressBook,
  CopySimple,
  Gift
} from '@phosphor-icons/react';
import Bed from '../../components/icons/RoomBedIcon';
import api from '../../services/api';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  buildGuestNameSlots,
  createEmptyGuestNameRows,
  getCompanionSlotCount,
  getGuestPartyCount,
  getNamedCompanionCount,
  removeCompanionSlot,
  resizeCompanionSlots,
  serializeGuestNameSlots,
} from './guestGroupFormUtils';
import {
  getPhoneContactKey,
  isPhoneContactPickerSupported,
  pickPhoneContacts,
  readVCardContacts,
} from './phoneContactPicker';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';

const RSVP_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' }
];

const UPLOAD_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'uploaded', label: 'Uploaded' },
];

const SIDE_SEGMENTS = [
  { value: 'bride', label: 'Bride', icon: Heart },
  { value: 'all', label: 'All', icon: UsersThree },
  { value: 'groom', label: 'Groom', icon: UsersThree }
];

const normalizeGuestSide = (side) => (
  side === 'bride' || side === 'groom' ? side : 'both'
);

const normalizeItinerarySide = (side) => (
  side === 'bride' || side === 'groom' ? side : 'both'
);

const getDefaultItineraryEventIdsForGuestSide = (itineraryItems = [], guestSide = 'both') => {
  const normalizedGuestSide = normalizeGuestSide(guestSide);
  return itineraryItems
    .filter((item) => {
      if (!item?.id) return false;
      if (normalizedGuestSide === 'both') return true;
      const itinerarySide = normalizeItinerarySide(item.side);
      return itinerarySide === 'both' || itinerarySide === normalizedGuestSide;
    })
    .map((item) => item.id);
};

const DELETE_SELECTED_CONFIRMATION = 'DELETE SELECTED GUESTS';
const DELETE_ALL_CONFIRMATION = 'DELETE ALL GUESTS';
const GUEST_PAGE_SIZE = 50;
const GUEST_STATUS_OPTIONS = [
  { id: 'rsvp', label: 'RSVP response', icon: CheckCircle },
  { id: 'id_upload', label: 'Guest ID', icon: IdentificationCard },
  { id: 'arrival', label: 'Arrival details', icon: AirplaneTakeoff },
  { id: 'departure', label: 'Departure details', icon: AirplaneLanding },
  { id: 'room', label: 'Room allocation', icon: Bed },
  { id: 'guest_side', label: 'Bride or groom side', icon: Heart },
];
const LEGACY_GUEST_STATUS_FIELDS = GUEST_STATUS_OPTIONS.map((option) => option.id);
const GUEST_STATUS_VISUALS = {
  complete: {
    label: 'Done',
    icon: Check,
    container: 'border-[#BDD1C4] bg-[#F1F7F3] text-[#456A55]',
    marker: 'bg-[#5F806B] text-white ring-1 ring-white'
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    container: 'border-[#DDD3BE] bg-[#FCF9F1] text-[#93702F]',
    marker: 'bg-[#B98934] text-white ring-1 ring-white'
  },
  declined: {
    label: 'Declined',
    icon: X,
    container: 'border-[#E5C9C6] bg-[#FCF5F4] text-[#9B5F5A]',
    marker: 'bg-[#A86661] text-white ring-1 ring-white'
  },
  not_required: {
    label: 'Not required',
    icon: Minus,
    container: 'border-dashed border-[#D7DBD8] bg-[#F8F9F8] text-[#8A938E]',
    marker: 'bg-[#8B948F] text-white ring-1 ring-white'
  }
};
const DEFAULT_GUEST_FILTERS = {
  rsvp: 'all',
  idUpload: 'all',
  arrivalUpload: 'all',
  departureUpload: 'all',
  side: 'all',
  itineraryEventIds: []
};

const countActiveGuestFilters = (value = DEFAULT_GUEST_FILTERS) => {
  let count = 0;
  if (value.rsvp !== 'all') count += 1;
  if (value.idUpload !== 'all') count += 1;
  if (value.arrivalUpload !== 'all') count += 1;
  if (value.departureUpload !== 'all') count += 1;
  if (value.side !== 'all') count += 1;
  count += (value.itineraryEventIds || []).length;
  return count;
};

const createEmptyGuestGroupForm = (itineraryEventIds = [], requiresRoom = true) => ({
  names: createEmptyGuestNameRows(),
  email: '',
  phone: '',
  guest_side: 'both',
  requires_room: requiresRoom,
  rooms_required: '',
  companion_count: 0,
  dietary_restrictions: '',
  tagged_itinerary_event_ids: [...itineraryEventIds]
});

const EMPTY_RECEIVED_GIFT_FORM = {
  kind: 'gift',
  description: '',
  amount: '',
  currency: 'INR',
  notes: '',
  received_on: ''
};

const giftToForm = (gift) => ({
  ...EMPTY_RECEIVED_GIFT_FORM,
  ...(gift || {}),
  amount: gift?.amount ?? '',
  received_on: gift?.received_on || ''
});

const formatGiftAmount = (gift) => {
  const amount = Number(gift?.amount);
  if (!Number.isFinite(amount)) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: gift?.currency || 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (_error) {
    return `${gift?.currency || 'INR'} ${amount.toLocaleString()}`;
  }
};

const formatGiftReceivedDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return value;
  return parsedDate.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const isGeneratedAccompanyingGuest = (guest) => (
  ['manager', 'rsvp'].includes(guest?.accompanying_guest_source)
  && Boolean(guest?.accompanying_owner_guest_id)
);

const getGuestGroupAccompanyingCount = (group = []) => {
  const savedCount = group.reduce((maximum, guest) => {
    const value = Number.parseInt(guest?.accompanying_guests || 0, 10);
    return Number.isNaN(value) ? maximum : Math.max(maximum, value);
  }, 0);
  return Math.max(savedCount, 0);
};

const FILTER_TONES = {
  rose: {
    icon: 'bg-[#F8ECEA] text-[#A66A62]',
    glow: 'from-[#FFF8F7] to-white',
    value: 'text-[#7C4741]'
  },
  gold: {
    icon: 'bg-[#F7EEDB] text-[#9A7432]',
    glow: 'from-[#FFF9ED] to-white',
    value: 'text-[#795A24]'
  },
  sage: {
    icon: 'bg-[#EEF3EA] text-[#68795F]',
    glow: 'from-[#F7FAF4] to-white',
    value: 'text-[#56684D]'
  },
  plum: {
    icon: 'bg-[#F2ECF4] text-[#765E7D]',
    glow: 'from-[#FBF7FC] to-white',
    value: 'text-[#634B6A]'
  }
};

const getOptionLabel = (options, value) => (
  options.find((option) => option.value === value)?.label || options[0]?.label || 'All'
);

const PremiumFilterSelect = ({ value, onValueChange, label, icon: Icon, options, tone = 'gold' }) => {
  const selectedLabel = getOptionLabel(options, value);
  const toneClass = FILTER_TONES[tone] || FILTER_TONES.gold;

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className={cn(
          'h-auto min-h-[56px] gap-2.5 rounded-xl border-[#E9E2D8] bg-gradient-to-br px-3 py-2 text-left shadow-[0_8px_20px_rgba(47,42,38,0.06)] transition-all duration-200',
          'hover:-translate-y-0.5 hover:border-[#D8CDBD] hover:shadow-[0_12px_24px_rgba(47,42,38,0.09)] focus:ring-2 focus:ring-[#C9A86A]/25',
          toneClass.glow
        )}
      >
        <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', toneClass.icon)}>
          <Icon className="h-3.5 w-3.5" weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="block text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-[#8A8178]">
            {label}
          </span>
          <span className={cn('mt-0.5 block truncate text-[0.8125rem] font-semibold', toneClass.value)}>
            {selectedLabel}
          </span>
        </div>
      </SelectTrigger>
      <SelectContent className="rounded-xl border-[#E9E2D8] bg-white p-1 shadow-[0_16px_36px_rgba(47,42,38,0.13)]">
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className="rounded-lg px-3 py-2 text-sm text-[#2F2A26] focus:bg-[#F8F6F2] focus:text-[#2F2A26]"
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const formatSkippedGuestRows = (skipped = []) => {
  if (!skipped.length) {
    return 'No importable rows were found. Use the guest bulk template and make sure name and phone are filled.';
  }

  const preview = skipped.slice(0, 4).map((row) => {
    const rowLabel = row.row ? `Row ${row.row}` : 'Row';
    const guestName = row.name ? ` (${row.name})` : '';
    return `${rowLabel}${guestName}: ${row.reason}`;
  });

  if (skipped.length > preview.length) {
    preview.push(`...and ${skipped.length - preview.length} more`);
  }

  return preview.join('\n');
};

export default function GuestManagement() {
  const {
    user,
    currentEvent,
    hasAdminAccess,
    isEventManager,
    refreshCurrentEvent,
  } = useAuth();
  const navigate = useNavigate();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const visibleGuestStatusOptions = React.useMemo(
    () => GUEST_STATUS_OPTIONS.filter((option) => (
      !(['id_upload', 'arrival', 'departure'].includes(option.id) && !eventTypeConfig.features.showTravelAndId)
      && !(option.id === 'room' && !eventTypeConfig.features.showAccommodation)
      && !(option.id === 'guest_side' && !eventTypeConfig.features.showGuestSides)
    )),
    [
      eventTypeConfig.features.showAccommodation,
      eventTypeConfig.features.showGuestSides,
      eventTypeConfig.features.showTravelAndId,
    ]
  );
  const [idPreviewDialog, setIdPreviewDialog] = useState(null);
  const [searchParams] = useSearchParams();
  const focusGuestId = searchParams.get('focus');
  useEffect(() => {
    if (!focusGuestId) return undefined;
    let clearTimer = null;
    let attempt = 0;
    const poll = window.setInterval(() => {
      attempt += 1;
      const node = document.querySelector(`[data-testid="guest-row-${CSS.escape(focusGuestId)}"]`);
      if (node) {
        window.clearInterval(poll);
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        node.classList.add('ring-2', 'ring-wedding-sage', 'ring-offset-2', 'animate-pulse');
        clearTimer = window.setTimeout(() => node.classList.remove('animate-pulse'), 2400);
      } else if (attempt >= 20) {
        window.clearInterval(poll);
      }
    }, 300);
    return () => {
      window.clearInterval(poll);
      if (clearTimer) window.clearTimeout(clearTimer);
    };
  }, [focusGuestId]);
  const [guests, setGuests] = useState([]);
  const [eventRsvps, setEventRsvps] = useState([]);
  const [guestPartyTotal, setGuestPartyTotal] = useState(0);
  const [hasMoreGuests, setHasMoreGuests] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMoreGuests, setLoadingMoreGuests] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(searchParams.get('action') === 'add');
  const [showBulkDialog, setShowBulkDialog] = useState(searchParams.get('action') === 'bulk');
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState('selected');
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState('');
  const [deletingAllGuests, setDeletingAllGuests] = useState(false);
  const [selectedBulkGuestIds, setSelectedBulkGuestIds] = useState([]);
  const [groupingGuests, setGroupingGuests] = useState(false);
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [downloadingGuestTemplate, setDownloadingGuestTemplate] = useState(false);
  const [exportingGuests, setExportingGuests] = useState(false);
  const [manualTravelEntry, setManualTravelEntry] = useState(null);
  const [uploadingGuestId, setUploadingGuestId] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showFullGuestGroup, setShowFullGuestGroup] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [newGuest, setNewGuest] = useState(createEmptyGuestGroupForm());
  const [newGuestItineraryCustomized, setNewGuestItineraryCustomized] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_GUEST_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_GUEST_FILTERS);
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);
  const [showStatusFieldsDialog, setShowStatusFieldsDialog] = useState(false);
  const [draftStatusFields, setDraftStatusFields] = useState([]);
  const [savingStatusFields, setSavingStatusFields] = useState(false);
  const [importingPhoneContacts, setImportingPhoneContacts] = useState(false);
  const [showContactFileDialog, setShowContactFileDialog] = useState(false);
  const [showContactSelectionDialog, setShowContactSelectionDialog] = useState(false);
  const [phoneContacts, setPhoneContacts] = useState([]);
  const [loadingPhoneContacts, setLoadingPhoneContacts] = useState(false);
  const [selectedPhoneContactKeys, setSelectedPhoneContactKeys] = useState([]);
  const [phoneContactSearch, setPhoneContactSearch] = useState('');
  const [addingSelectedPhoneContacts, setAddingSelectedPhoneContacts] = useState(false);
  const contactFileInputRef = useRef(null);
  const phoneContactEventIdRef = useRef(null);
  const [giftDialogGuest, setGiftDialogGuest] = useState(null);
  const [receivedGift, setReceivedGift] = useState(null);
  const [giftForm, setGiftForm] = useState(EMPTY_RECEIVED_GIFT_FORM);
  const [isGiftEditing, setIsGiftEditing] = useState(false);
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftSaving, setGiftSaving] = useState(false);
  const [thankYouGenerating, setThankYouGenerating] = useState(false);
  const [thankYouTone, setThankYouTone] = useState('warm');
  const [thankYouSender, setThankYouSender] = useState('');
  const [mentionGiftAmount, setMentionGiftAmount] = useState(false);

  // WhatsApp state
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [whatsappReadiness, setWhatsappReadiness] = useState(null);
  const [selectedGuestIds, setSelectedGuestIds] = useState([]);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [whatsappConfig, setWhatsappConfig] = useState({ registration_base_url: '' });
  const [whatsappMode, setWhatsappMode] = useState('invite');
  const [pendingWhatsAppSend, setPendingWhatsAppSend] = useState(null);
  const [invitePacks, setInvitePacks] = useState([]);
  const [loadingInvitePacks, setLoadingInvitePacks] = useState(false);
  const [selectedInvitePackId, setSelectedInvitePackId] = useState('__auto__');
  const loadMoreGuestsRef = React.useRef(null);
  const guestListRequestRef = React.useRef(0);
  const itineraryItems = React.useMemo(
    () => eventTypeConfig.features.showItinerary ? currentEvent?.itinerary || [] : [],
    [currentEvent?.itinerary, eventTypeConfig.features.showItinerary]
  );
  const sortedItineraryItems = React.useMemo(() => {
    const toSortValue = (item, index) => {
      const parsedDate = item?.date ? new Date(item.date).getTime() : Number.POSITIVE_INFINITY;
      const datePart = Number.isFinite(parsedDate) ? parsedDate : Number.POSITIVE_INFINITY;
      const timePart = item?.start_time ? String(item.start_time).padStart(5, '0') : '99:99';
      return { datePart, timePart, index };
    };

    return itineraryItems
      .map((item, index) => ({ item, sortValue: toSortValue(item, index) }))
      .sort((a, b) => {
        if (a.sortValue.datePart !== b.sortValue.datePart) return a.sortValue.datePart - b.sortValue.datePart;
        if (a.sortValue.timePart !== b.sortValue.timePart) return a.sortValue.timePart.localeCompare(b.sortValue.timePart);
        return a.sortValue.index - b.sortValue.index;
      })
      .map(({ item }) => item);
  }, [itineraryItems]);
  const allItineraryEventIds = React.useMemo(
    () => sortedItineraryItems.map((item) => item.id).filter(Boolean),
    [sortedItineraryItems]
  );
  const defaultNewGuestItineraryEventIds = React.useMemo(
    () => getDefaultItineraryEventIdsForGuestSide(sortedItineraryItems, newGuest.guest_side),
    [newGuest.guest_side, sortedItineraryItems]
  );

  const fetchPersistedPhoneContacts = useCallback(async ({ showError = true } = {}) => {
    if (!currentEvent?.id) return [];
    const requestedEventId = currentEvent.id;
    try {
      setLoadingPhoneContacts(true);
      const response = await api.get(`/events/${requestedEventId}/guest-contacts`);
      const contacts = Array.isArray(response.data?.contacts) ? response.data.contacts : [];
      if (phoneContactEventIdRef.current !== requestedEventId) return [];
      setPhoneContacts(contacts);
      return contacts;
    } catch (error) {
      if (showError && phoneContactEventIdRef.current === requestedEventId) {
        toast.error(error.response?.data?.detail || 'Failed to load saved contacts for this event');
      }
      throw error;
    } finally {
      if (phoneContactEventIdRef.current === requestedEventId) setLoadingPhoneContacts(false);
    }
  }, [currentEvent?.id]);

  useEffect(() => {
    let active = true;
    phoneContactEventIdRef.current = currentEvent?.id || null;
    setPhoneContacts([]);
    setSelectedPhoneContactKeys([]);
    setPhoneContactSearch('');
    setLoadingPhoneContacts(false);
    setShowContactFileDialog(false);
    setShowContactSelectionDialog(false);
    if (currentEvent?.id) {
      fetchPersistedPhoneContacts().catch(() => {
        if (active) setPhoneContacts([]);
      });
    }
    return () => {
      active = false;
    };
  }, [currentEvent?.id, fetchPersistedPhoneContacts]);

  useEffect(() => {
    if (!showAddDialog || newGuestItineraryCustomized) return;

    setNewGuest((current) => {
      const currentIds = current.tagged_itinerary_event_ids || [];
      const alreadySelected = currentIds.length === defaultNewGuestItineraryEventIds.length
        && defaultNewGuestItineraryEventIds.every((itemId) => currentIds.includes(itemId));
      return alreadySelected
        ? current
        : { ...current, tagged_itinerary_event_ids: [...defaultNewGuestItineraryEventIds] };
    });
  }, [defaultNewGuestItineraryEventIds, newGuestItineraryCustomized, showAddDialog]);
  const itineraryById = React.useMemo(
    () => new Map(sortedItineraryItems.map((item) => [item.id, item])),
    [sortedItineraryItems]
  );
  const filterStorageKey = React.useMemo(
    () => (user?.id && currentEvent?.id ? `guest-management-filters:${user.id}:${currentEvent.id}` : null),
    [user?.id, currentEvent?.id]
  );
  const enabledStatusFields = React.useMemo(
    () => {
      const enabled = new Set(
        Array.isArray(currentEvent?.guest_status_fields)
          ? currentEvent.guest_status_fields
          : LEGACY_GUEST_STATUS_FIELDS
      );
      return new Set(
        visibleGuestStatusOptions
          .map((option) => option.id)
          .filter((fieldId) => enabled.has(fieldId))
      );
    },
    [currentEvent?.guest_status_fields, visibleGuestStatusOptions]
  );
  const hasEnabledStatusFields = enabledStatusFields.size > 0;

  const normalizeFilters = useCallback((value = {}) => ({
    ...DEFAULT_GUEST_FILTERS,
    ...value,
    itineraryEventIds: eventTypeConfig.features.showItinerary && Array.isArray(value.itineraryEventIds)
      ? value.itineraryEventIds.filter(Boolean)
      : []
  }), [eventTypeConfig.features.showItinerary]);

  const getItineraryItemLabel = (item) => {
    const dateLabel = item.date ? new Date(item.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
    return [item.name, dateLabel, item.start_time].filter(Boolean).join(' - ');
  };

  const getItineraryDateLabel = (item) => {
    const dateLabel = item?.date ? new Date(item.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : 'Date not set';
    return [dateLabel, item?.start_time].filter(Boolean).join(' at ');
  };

  const normalizeGuestGroupForm = (form) => {
    const slotSummary = serializeGuestNameSlots(form.names || []);
    const names = slotSummary.names;

    if (!form.phone?.trim()) {
      toast.error('Phone number is required');
      return null;
    }
    if (slotSummary.anchorNameMissing) {
      toast.error('Enter the invited guest name');
      return null;
    }
    if (slotSummary.unnamedCompanionCount > 50) {
      toast.error('Unnamed accompanying guest count cannot exceed 50');
      return null;
    }

    const roomCountValue = String(form.rooms_required ?? '').trim();
    const roomsRequired = roomCountValue ? Number(roomCountValue) : null;
    if (
      form.requires_room === true
      && roomsRequired !== null
      && (!Number.isInteger(roomsRequired) || roomsRequired < 1 || roomsRequired > 50)
    ) {
      toast.error('Rooms required must be a whole number between 1 and 50');
      return null;
    }

    const validItineraryIds = new Set(itineraryItems.map((item) => item.id).filter(Boolean));
    const taggedIds = (form.tagged_itinerary_event_ids || []).filter((id) => validItineraryIds.has(id));
    if ((form.tagged_itinerary_event_ids || []).length !== taggedIds.length) {
      toast.error('One or more itinerary selections are no longer valid');
      return null;
    }

    return {
      phone: form.phone.trim(),
      email: form.email?.trim() || null,
      guest_side: eventTypeConfig.features.showGuestSides
        ? normalizeGuestSide(form.guest_side)
        : 'both',
      requires_room: eventTypeConfig.features.showAccommodation && form.requires_room === true,
      rooms_required: (
        eventTypeConfig.features.showAccommodation
        && form.requires_room === true
      ) ? roomsRequired : null,
      accompanying_guests: slotSummary.unnamedCompanionCount,
      dietary_restrictions: form.dietary_restrictions?.trim() || null,
      tagged_itinerary_event_ids: taggedIds,
      names,
    };
  };

  const updateGuestNameRow = (setter, index, value) => {
    setter((current) => ({
      ...current,
      names: current.names.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        return typeof row === 'string' ? value : { ...row, name: value };
      })
    }));
  };

  const removeGuestNameRow = (setter, index) => {
    setter((current) => {
      const nextNames = removeCompanionSlot(current.names || [], index);
      return {
        ...current,
        names: nextNames,
        companion_count: getCompanionSlotCount(nextNames),
      };
    });
  };

  const toggleFilterItinerary = (itemId, checked) => {
    setDraftFilters((current) => {
      const currentIds = current.itineraryEventIds || [];
      return {
        ...current,
        itineraryEventIds: checked
          ? [...new Set([...currentIds, itemId])]
          : currentIds.filter((id) => id !== itemId)
      };
    });
  };

  const getActiveFilterCount = (value = filters) => countActiveGuestFilters(value);

  const handleFiltersOpenChange = (open) => {
    setShowFiltersSheet(open);
    if (open) {
      setDraftFilters(normalizeFilters(filters));
    }
  };

  const applyFilters = () => {
    setFilters(normalizeFilters(draftFilters));
    setShowFiltersSheet(false);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_GUEST_FILTERS);
    setDraftFilters(DEFAULT_GUEST_FILTERS);
    if (filterStorageKey) {
      localStorage.removeItem(filterStorageKey);
    }
  };

  const updateAppliedFilters = (updater) => {
    setFilters((current) => normalizeFilters(updater(normalizeFilters(current))));
    setDraftFilters((current) => normalizeFilters(updater(normalizeFilters(current))));
  };

  const removeFilterChip = (type, value) => {
    updateAppliedFilters((current) => {
      const next = { ...current };
      if (type === 'itinerary') {
        next.itineraryEventIds = (next.itineraryEventIds || []).filter((itemId) => itemId !== value);
      } else {
        next[type] = DEFAULT_GUEST_FILTERS[type];
      }
      return next;
    });
  };

  const getActiveFilterChips = () => {
    const chips = [];
    if (eventTypeConfig.features.showGuestSides && filters.side !== 'all') {
      chips.push({ key: 'side', label: `Side: ${getGuestSideLabel(filters.side)}`, type: 'side' });
    }
    if (filters.rsvp !== 'all') {
      chips.push({ key: 'rsvp', label: `RSVP: ${getOptionLabel(RSVP_FILTER_OPTIONS, filters.rsvp)}`, type: 'rsvp' });
    }
    if (eventTypeConfig.features.showTravelAndId && filters.idUpload !== 'all') {
      chips.push({ key: 'idUpload', label: `ID: ${getOptionLabel(UPLOAD_FILTER_OPTIONS, filters.idUpload)}`, type: 'idUpload' });
    }
    if (eventTypeConfig.features.showTravelAndId && filters.arrivalUpload !== 'all') {
      chips.push({ key: 'arrivalUpload', label: `Arrival: ${getOptionLabel(UPLOAD_FILTER_OPTIONS, filters.arrivalUpload)}`, type: 'arrivalUpload' });
    }
    if (eventTypeConfig.features.showTravelAndId && filters.departureUpload !== 'all') {
      chips.push({ key: 'departureUpload', label: `Departure: ${getOptionLabel(UPLOAD_FILTER_OPTIONS, filters.departureUpload)}`, type: 'departureUpload' });
    }
    (filters.itineraryEventIds || []).forEach((itemId) => {
      const item = itineraryById.get(itemId);
      chips.push({
        key: `itinerary-${itemId}`,
        label: item ? `Itinerary: ${item.name}` : `Itinerary: ${itemId}`,
        type: 'itinerary',
        value: itemId,
      });
    });
    return chips;
  };

  useEffect(() => {
    if (!filterStorageKey) {
      setFilters(DEFAULT_GUEST_FILTERS);
      setDraftFilters(DEFAULT_GUEST_FILTERS);
      return;
    }
    try {
      const saved = localStorage.getItem(filterStorageKey);
      const restored = saved ? normalizeFilters(JSON.parse(saved)) : DEFAULT_GUEST_FILTERS;
      setFilters(restored);
      setDraftFilters(restored);
    } catch (error) {
      setFilters(DEFAULT_GUEST_FILTERS);
      setDraftFilters(DEFAULT_GUEST_FILTERS);
    }
  }, [filterStorageKey, normalizeFilters]);

  useEffect(() => {
    if (!filterStorageKey) return;
    const normalized = normalizeFilters(filters);
    if (countActiveGuestFilters(normalized) === 0) {
      localStorage.removeItem(filterStorageKey);
      return;
    }
    localStorage.setItem(filterStorageKey, JSON.stringify(normalized));
  }, [filterStorageKey, filters, normalizeFilters]);

  useEffect(() => {
    fetchGuests();
  }, [currentEvent?.id, searchQuery, filters]);

  useEffect(() => {
    let active = true;
    if (!currentEvent?.id) {
      setEventRsvps([]);
      return undefined;
    }

    api.get(`/events/${currentEvent.id}/rsvp`)
      .then((response) => {
        if (active) setEventRsvps(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error('Failed to fetch event RSVP details:', error);
        if (active) setEventRsvps([]);
      });
    return () => {
      active = false;
    };
  }, [currentEvent?.id]);

  useEffect(() => {
    if (currentEvent?.id) {
      const scheduleReadinessCheck = window.requestIdleCallback || ((callback) => setTimeout(callback, 600));
      const cancelReadinessCheck = window.cancelIdleCallback || clearTimeout;
      const handle = scheduleReadinessCheck(() => checkWhatsAppReadiness());
      return () => cancelReadinessCheck(handle);
    }
  }, [currentEvent?.id]);

  useEffect(() => {
    setSelectedBulkGuestIds((selectedIds) => {
      const activeGuestIds = new Set(guests.map((guest) => guest.id));
      return selectedIds.filter((guestId) => activeGuestIds.has(guestId));
    });
  }, [guests]);

  useEffect(() => {
    setSelectedBulkGuestIds([]);
  }, [currentEvent?.id, searchQuery, filters]);

  useEffect(() => {
    setShowFullGuestGroup(false);
  }, [selectedGuest?.id, showGuestDialog]);

  const getGuestListFilterParams = () => ({
    search: searchQuery.trim() || undefined,
    rsvp: filters.rsvp,
    side: filters.side,
    id_upload: filters.idUpload,
    arrival_upload: filters.arrivalUpload,
    departure_upload: filters.departureUpload,
    itinerary_event_ids: (filters.itineraryEventIds || []).length
      ? filters.itineraryEventIds.join(',')
      : undefined
  });

  const fetchGuests = async ({ append = false } = {}) => {
    if (!currentEvent?.id) return;
    const currentOffset = append ? guests.length : 0;
    const requestId = guestListRequestRef.current + 1;
    guestListRequestRef.current = requestId;
    const isLatestRequest = () => guestListRequestRef.current === requestId;

    if (append) {
      setLoadingMoreGuests(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await api.get(`/events/${currentEvent.id}/guests/list`, {
        params: {
          skip: currentOffset,
          limit: GUEST_PAGE_SIZE,
          ...getGuestListFilterParams()
        }
      });

      if (!isLatestRequest()) return;

      const nextGuests = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.guests)
          ? response.data.guests
          : [];
      const totalCount = Number(response.headers['x-total-count'] || nextGuests.length);
      const safeTotalCount = Number.isNaN(totalCount) ? nextGuests.length : totalCount;
      const totalPartyCount = Number(response.headers['x-total-party-count'] || safeTotalCount);
      setGuestPartyTotal(Number.isNaN(totalPartyCount) ? safeTotalCount : totalPartyCount);
      setHasMoreGuests(response.headers['x-has-more'] === 'true');
      setGuests((currentGuests) => {
        const currentGuestList = Array.isArray(currentGuests) ? currentGuests : [];
        if (!append) return nextGuests;

        const existingIds = new Set(currentGuestList.map((guest) => guest.id));
        const uniqueNextGuests = nextGuests.filter((guest) => !existingIds.has(guest.id));
        return [...currentGuestList, ...uniqueNextGuests];
      });
    } catch (error) {
      if (!isLatestRequest()) return;
      toast.error('Failed to fetch guests');
    } finally {
      if (append) {
        setLoadingMoreGuests(false);
      } else if (isLatestRequest()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleChatMutation = (event) => {
      if (event.detail?.eventId === currentEvent?.id) fetchGuests();
    };
    window.addEventListener('galago-chat-mutation', handleChatMutation);
    return () => window.removeEventListener('galago-chat-mutation', handleChatMutation);
  }, [currentEvent?.id]);

  useEffect(() => {
    const target = loadMoreGuestsRef.current;
    if (!target || !hasMoreGuests || loading || loadingMoreGuests) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchGuests({ append: true });
        }
      },
      { rootMargin: '300px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMoreGuests, loading, loadingMoreGuests, guests.length, currentEvent?.id, searchQuery, filters]);

  const checkWhatsAppReadiness = async () => {
    try {
      const response = await api.get(`/events/${currentEvent.id}/whatsapp/readiness`);
      setWhatsappReadiness(response.data);
    } catch (error) {
      console.error('Failed to check WhatsApp readiness:', error);
    }
  };

  const fetchInvitePacks = async () => {
    if (!currentEvent?.id) return;
    setLoadingInvitePacks(true);
    try {
      const response = await api.get(`/events/${currentEvent.id}/invite-packs`);
      setInvitePacks(response.data?.items || []);
    } catch (error) {
      console.error('Failed to load invite packs:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load invite media'));
    } finally {
      setLoadingInvitePacks(false);
    }
  };

  const getApiErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return detail.message;
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item?.msg) return item.msg;
          if (item?.message) return item.message;
          return null;
        })
        .filter(Boolean);
      if (messages.length > 0) return messages.join(', ');
    }
    return fallback;
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    const payload = normalizeGuestGroupForm(newGuest);
    if (!payload) return;

    try {
      const response = await api.post(`/events/${currentEvent.id}/guests/group`, payload);
      toast.success(response.data?.message || 'Guest group added');
      setNewGuest(createEmptyGuestGroupForm(allItineraryEventIds, eventTypeConfig.features.defaultGuestRequiresRoom));
      setNewGuestItineraryCustomized(false);
      fetchGuests();
      checkWhatsAppReadiness();
      setTimeout(() => setShowAddDialog(false), 100);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to add guest'));
    }
  };

  const addContactSelectionAsGuests = async (result) => {
    if (result.selectedCount === 0) return;

    const contactsWithoutRequiredDetails = result.selectedCount - result.contacts.length;
    if (result.contacts.length === 0) {
      toast.error('No contacts could be imported', {
        description: 'The selected contacts must include both a name and phone number.',
      });
      return { guestsAdded: 0, skippedCount: contactsWithoutRequiredDetails };
    }

    let guestsAdded = 0;
    let skippedCount = contactsWithoutRequiredDetails;
    for (let index = 0; index < result.contacts.length; index += 500) {
      const response = await api.post(`/events/${currentEvent.id}/guests/import-contacts`, {
        contacts: result.contacts.slice(index, index + 500),
        guest_side: 'both',
        requires_room: eventTypeConfig.features.defaultGuestRequiresRoom,
        tagged_itinerary_event_ids: allItineraryEventIds,
      });
      guestsAdded += response.data?.guests_added || 0;
      skippedCount += response.data?.skipped_count || 0;
    }

    if (guestsAdded > 0) {
      toast.success(`Added ${guestsAdded} contact${guestsAdded === 1 ? '' : 's'} as guests`, {
        description: skippedCount > 0
          ? `${skippedCount} selection${skippedCount === 1 ? ' was' : 's were'} skipped because details were missing, invalid, or already present.`
          : undefined,
      });
      fetchGuests();
      checkWhatsAppReadiness();
    } else {
      toast.error('No new guests were added', {
        description: skippedCount > 0
          ? 'The selected contacts were missing details, had invalid phone numbers, or were already in the guest list.'
          : undefined,
      });
    }
    return { guestsAdded, skippedCount };
  };

  const stagePhoneContacts = async (result) => {
    if (result.selectedCount === 0) return;

    const contactsWithoutRequiredDetails = Math.max(0, result.selectedCount - result.contacts.length);
    const importIssues = Array.isArray(result.skipped) ? [...result.skipped] : [];
    if (importIssues.length === 0 && contactsWithoutRequiredDetails > 0) {
      importIssues.push({
        reason: `${contactsWithoutRequiredDetails} contacts did not include both a name and phone number`,
      });
    }
    const formatImportIssues = () => {
      const details = importIssues.slice(0, 3).map((item) => {
        const label = item.name || item.phone || 'Contact';
        return `${label}: ${item.reason || 'Could not be loaded'}`;
      });
      if (importIssues.length > 3) details.push(`and ${importIssues.length - 3} more`);
      return details.join('; ');
    };
    if (result.contacts.length === 0) {
      toast.error('No contacts were loaded', {
        description: formatImportIssues() || 'Contacts must include both a name and phone number.',
        duration: 9000,
      });
      return;
    }

    let contactsAdded = 0;
    let contactsExisting = 0;
    let skippedCount = contactsWithoutRequiredDetails;
    for (let index = 0; index < result.contacts.length; index += 500) {
      const response = await api.post(`/events/${currentEvent.id}/guest-contacts`, {
        contacts: result.contacts.slice(index, index + 500),
      });
      contactsAdded += response.data?.contacts_added || 0;
      contactsExisting += response.data?.contacts_existing || 0;
      skippedCount += response.data?.skipped_count || 0;
      if (Array.isArray(response.data?.skipped)) importIssues.push(...response.data.skipped);
    }

    const persistedContacts = await fetchPersistedPhoneContacts({ showError: false });
    setShowContactFileDialog(false);
    setShowContactSelectionDialog(true);
    if (skippedCount > 0) {
      const availableCount = contactsAdded + contactsExisting;
      toast.warning(`${availableCount} of ${result.selectedCount} selected contact${result.selectedCount === 1 ? '' : 's'} available`, {
        description: formatImportIssues(),
        duration: 9000,
      });
    } else if (contactsAdded > 0) {
      toast.success(`Loaded ${contactsAdded} new contact${contactsAdded === 1 ? '' : 's'}`, {
        description: contactsExisting > 0
          ? `${contactsExisting} selected contact${contactsExisting === 1 ? ' was' : 's were'} already loaded.`
          : `${persistedContacts.length} total contact${persistedContacts.length === 1 ? '' : 's'} saved for this event.`,
      });
    } else if (contactsExisting > 0) {
      toast.info(`${contactsExisting} selected contact${contactsExisting === 1 ? ' was' : 's were'} already loaded`, {
        description: `No duplicate was created. ${persistedContacts.length} total contact${persistedContacts.length === 1 ? '' : 's'} remain available.`,
      });
    }
  };

  const handleLoadPhoneContacts = async () => {
    if (!isPhoneContactPickerSupported()) {
      setShowContactSelectionDialog(false);
      setShowContactFileDialog(true);
      return;
    }

    setImportingPhoneContacts(true);
    try {
      await stagePhoneContacts(await pickPhoneContacts());
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (error?.message === 'CONTACT_PICKER_UNSUPPORTED') {
        setShowContactFileDialog(true);
        return;
      }
      const pickerMessages = {
        CONTACT_FIELDS_UNAVAILABLE: 'This device did not allow contact names and phone numbers to be shared.',
      };
      toast.error(pickerMessages[error?.message] || getApiErrorMessage(error, 'Failed to import phone contacts'));
    } finally {
      setImportingPhoneContacts(false);
    }
  };

  const handleOpenPhoneContacts = () => {
    if (phoneContacts.length > 0) {
      setShowContactSelectionDialog(true);
      return;
    }
    handleLoadPhoneContacts();
  };

  const handleVCardContactFile = async (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (files.length === 0) return;

    setImportingPhoneContacts(true);
    try {
      const results = [];
      const failedFiles = [];
      for (const file of files) {
        try {
          results.push(await readVCardContacts(file));
        } catch (error) {
          failedFiles.push({ file, error });
        }
      }

      if (results.length === 0) throw failedFiles[0]?.error || new Error('VCARD_FILE_INVALID');

      await stagePhoneContacts({
        contacts: results.flatMap((result) => result.contacts),
        selectedCount: results.reduce((total, result) => total + result.selectedCount, 0),
        skipped: results.flatMap((result) => result.skipped || []),
      });
      if (failedFiles.length > 0) {
        toast.warning(`${failedFiles.length} file${failedFiles.length === 1 ? '' : 's'} could not be read`);
      }
    } catch (error) {
      const fileMessages = {
        VCARD_FILE_UNREADABLE: 'This contacts file could not be read.',
        VCARD_FILE_TOO_LARGE: 'The contacts file must be 10 MB or smaller.',
        VCARD_FILE_INVALID: 'Choose a valid vCard contacts file (.vcf).',
      };
      toast.error(fileMessages[error?.message] || getApiErrorMessage(error, 'Failed to import the contacts file'));
    } finally {
      input.value = '';
      setImportingPhoneContacts(false);
    }
  };

  const filteredPhoneContacts = phoneContacts.filter((contact) => {
    const query = phoneContactSearch.trim().toLocaleLowerCase();
    if (!query) return true;
    return contact.name.toLocaleLowerCase().includes(query) || contact.phone.toLocaleLowerCase().includes(query);
  });
  const selectedPhoneContactKeySet = new Set(selectedPhoneContactKeys);
  const selectedPhoneContacts = phoneContacts.filter((contact) => (
    !contact.is_guest && selectedPhoneContactKeySet.has(getPhoneContactKey(contact))
  ));
  const selectableFilteredPhoneContacts = filteredPhoneContacts.filter((contact) => !contact.is_guest);
  const visiblePhoneContactKeys = selectableFilteredPhoneContacts.map(getPhoneContactKey);
  const selectedVisiblePhoneContactCount = visiblePhoneContactKeys.filter((key) => (
    selectedPhoneContactKeySet.has(key)
  )).length;
  const allVisiblePhoneContactsSelected = visiblePhoneContactKeys.length > 0
    && selectedVisiblePhoneContactCount === visiblePhoneContactKeys.length;

  const togglePhoneContact = (contactKey, checked) => {
    setSelectedPhoneContactKeys((current) => (
      checked
        ? Array.from(new Set([...current, contactKey]))
        : current.filter((key) => key !== contactKey)
    ));
  };

  const toggleVisiblePhoneContacts = (checked) => {
    setSelectedPhoneContactKeys((current) => {
      const visibleKeys = new Set(visiblePhoneContactKeys);
      if (!checked) return current.filter((key) => !visibleKeys.has(key));
      return Array.from(new Set([...current, ...visiblePhoneContactKeys]));
    });
  };

  const handleAddSelectedPhoneContacts = async () => {
    if (selectedPhoneContacts.length === 0) {
      toast.error('Select at least one contact');
      return;
    }

    setAddingSelectedPhoneContacts(true);
    try {
      const result = await addContactSelectionAsGuests({
        contacts: selectedPhoneContacts,
        selectedCount: selectedPhoneContacts.length,
      });
      if (result) {
        await fetchPersistedPhoneContacts({ showError: false });
        setSelectedPhoneContactKeys([]);
        if (result.guestsAdded > 0) setShowContactSelectionDialog(false);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to add selected contacts'));
    } finally {
      setAddingSelectedPhoneContacts(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/events/${currentEvent.id}/guests/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const guestsAdded = response.data?.guests_added || 0;
      const skippedRows = response.data?.skipped || [];
      const skippedCount = response.data?.skipped_count ?? skippedRows.length;

      if (guestsAdded === 0) {
        toast.error('No guests were added', {
          description: formatSkippedGuestRows(skippedRows),
          duration: 9000
        });
      } else if (skippedCount > 0) {
        toast.warning(response.data.message, {
          description: formatSkippedGuestRows(skippedRows),
          duration: 9000
        });
      } else {
        toast.success(response.data.message);
      }
      setShowBulkDialog(false);
      fetchGuests();
      checkWhatsAppReadiness();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to upload guests'));
    }
  };

  const handleDownloadGuestTemplate = async () => {
    if (!currentEvent?.id) return;
    try {
      setDownloadingGuestTemplate(true);
      const response = await api.get(`/events/${currentEvent.id}/guests/bulk-template`, {
        responseType: 'blob',
        params: { template_version: 8, _: Date.now() }
      });
      const templateVersion = response.headers['x-guest-template-version'];
      if (templateVersion !== '8') {
        throw new Error('The connected API is serving an older guest template. Refresh after the backend is updated.');
      }
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `guest_bulk_template_v8_${currentEvent.id}.xlsm`;

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/vnd.ms-excel.sheet.macroEnabled.12' })
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Guest template v8 downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download template');
    } finally {
      setDownloadingGuestTemplate(false);
    }
  };

  const handleExportGuests = async () => {
    if (!currentEvent?.id || exportingGuests) return;
    try {
      setExportingGuests(true);
      const response = await api.get(`/events/${currentEvent.id}/guests/export`, {
        responseType: 'blob',
        params: getGuestListFilterParams()
      });
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fallbackName = `${String(currentEvent.name || 'event').replace(/[^a-z0-9_-]+/gi, '_')}_guest_list.xlsx`;
      const filename = filenameMatch?.[1] || fallbackName;
      const blobUrl = window.URL.createObjectURL(new Blob(
        [response.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      ));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(getActiveFilterCount() > 0
        ? 'Filtered guest list exported'
        : 'Guest list exported');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to export guest list'));
    } finally {
      setExportingGuests(false);
    }
  };

  const refreshGuestAfterManualTravel = async (guestId) => {
    await fetchGuests();
    if (!guestId || selectedGuest?.id !== guestId) return;
    try {
      const response = await api.get(`/events/${currentEvent.id}/guests/${guestId}`);
      setSelectedGuest((current) => (
        current?.id === guestId ? { ...current, ...response.data } : current
      ));
    } catch (error) {
      toast.error('Travel details were saved, but the guest preview could not be refreshed');
    }
  };

  const handleToggleAdmin = async (guest) => {
    try {
      await api.put(`/events/${currentEvent.id}/guests/${guest.id}/admin?is_admin=${!guest.is_admin}`);
      toast.success(`Admin ${guest.is_admin ? 'removed' : 'granted'} for ${guest.name}`);
      fetchGuests();
    } catch (error) {
      toast.error('Failed to update admin status');
    }
  };

  const handleDeleteGuest = async (guest) => {
    if (!confirm(`Are you sure you want to delete ${guest.name}?`)) return;
    try {
      await api.delete(`/events/${currentEvent.id}/guests/${guest.id}`);
      toast.success('Guest deleted');
      fetchGuests();
      checkWhatsAppReadiness();
    } catch (error) {
      toast.error('Failed to delete guest');
    }
  };

  const defaultThankYouSender = () => (
    eventTypeConfig.isWedding
      ? [currentEvent?.bride_name, currentEvent?.groom_name].filter(Boolean).join(' & ')
      : (currentEvent?.host_names || []).join(' & ')
  );

  const setGiftRecordedIndicator = (guestId, hasGift) => {
    setGuests((current) => current.map((guest) => (
      guest.id === guestId ? { ...guest, has_received_gift: hasGift } : guest
    )));
    setSelectedGuest((current) => (
      current?.id === guestId ? { ...current, has_received_gift: hasGift } : current
    ));
  };

  const openReceivedGiftDialog = async (guest) => {
    setGiftDialogGuest(guest);
    setReceivedGift(null);
    setGiftForm(EMPTY_RECEIVED_GIFT_FORM);
    setIsGiftEditing(!guest.has_received_gift);
    setThankYouTone('warm');
    setThankYouSender(defaultThankYouSender());
    setMentionGiftAmount(false);
    try {
      setGiftLoading(true);
      const response = await api.get(`/events/${currentEvent.id}/guests/${guest.id}/received-gift`);
      const gift = response.data?.gift || null;
      setReceivedGift(gift);
      setGiftForm(giftToForm(gift));
      setIsGiftEditing(!gift);
      if (gift?.thank_you_tone) setThankYouTone(gift.thank_you_tone);
      if (gift?.thank_you_sender) setThankYouSender(gift.thank_you_sender);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the gift record');
    } finally {
      setGiftLoading(false);
    }
  };

  const persistReceivedGift = async ({ notify = true } = {}) => {
    if (!giftDialogGuest) return null;
    if (giftForm.kind === 'gift' && !giftForm.description.trim()) {
      toast.error('Describe the gift');
      return null;
    }
    if (giftForm.kind === 'cash' && (!giftForm.amount || Number(giftForm.amount) <= 0)) {
      toast.error('Enter the cash gift amount');
      return null;
    }
    try {
      setGiftSaving(true);
      const response = await api.put(
        `/events/${currentEvent.id}/guests/${giftDialogGuest.id}/received-gift`,
        {
          kind: giftForm.kind,
          description: giftForm.description.trim() || null,
          amount: giftForm.amount === '' ? null : Number(giftForm.amount),
          currency: giftForm.currency,
          notes: giftForm.notes.trim() || null,
          received_on: giftForm.received_on || null,
        }
      );
      setReceivedGift(response.data.gift);
      setGiftForm(giftToForm(response.data.gift));
      setIsGiftEditing(false);
      setGiftRecordedIndicator(giftDialogGuest.id, true);
      if (notify) toast.success(`Gift recorded for ${giftDialogGuest.name}`);
      return response.data.gift;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the gift record');
      return null;
    } finally {
      setGiftSaving(false);
    }
  };

  const removeReceivedGift = async () => {
    if (!giftDialogGuest || !receivedGift) return;
    if (!window.confirm(`Remove the gift record for ${giftDialogGuest.name}?`)) return;
    try {
      setGiftSaving(true);
      await api.delete(`/events/${currentEvent.id}/guests/${giftDialogGuest.id}/received-gift`);
      setReceivedGift(null);
      setGiftForm(EMPTY_RECEIVED_GIFT_FORM);
      setIsGiftEditing(true);
      setGiftRecordedIndicator(giftDialogGuest.id, false);
      toast.success('Gift record removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove the gift record');
    } finally {
      setGiftSaving(false);
    }
  };

  const generateThankYouDraft = async () => {
    const savedGift = await persistReceivedGift({ notify: false });
    if (!savedGift) return;
    try {
      setThankYouGenerating(true);
      const response = await api.post(
        `/events/${currentEvent.id}/guests/${giftDialogGuest.id}/thank-you-draft`,
        {
          tone: thankYouTone,
          sender_name: thankYouSender.trim() || null,
          mention_amount: mentionGiftAmount,
        }
      );
      const updatedGift = {
        ...savedGift,
        thank_you_draft: response.data.draft,
        thank_you_drafted_at: response.data.drafted_at,
        thank_you_tone: thankYouTone,
        thank_you_sender: thankYouSender.trim() || defaultThankYouSender(),
      };
      setReceivedGift(updatedGift);
      setGiftForm(giftToForm(updatedGift));
      toast.success('Thank-you note drafted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not draft a thank-you note');
    } finally {
      setThankYouGenerating(false);
    }
  };

  const copyThankYouDraft = async () => {
    if (!receivedGift?.thank_you_draft) return;
    try {
      await navigator.clipboard.writeText(receivedGift.thank_you_draft);
      toast.success('Thank-you note copied');
    } catch (_error) {
      toast.error('Could not copy the note');
    }
  };

  const handleBulkDeleteGuests = async () => {
    const isDeleteAll = bulkDeleteMode === 'all';
    if (
      !currentEvent?.id ||
      (isDeleteAll && bulkDeleteConfirmation !== DELETE_ALL_CONFIRMATION) ||
      (!isDeleteAll && selectedBulkGuestIds.length === 0)
    ) return;

    setDeletingAllGuests(true);
    try {
      const payload = isDeleteAll
        ? { guest_ids: [], confirmation: bulkDeleteConfirmation, delete_all: true }
        : { guest_ids: selectedBulkGuestIds, confirmation: DELETE_SELECTED_CONFIRMATION };
      const response = await api.post(`/events/${currentEvent.id}/guests/bulk-delete`, {
        ...payload
      });
      toast.success(response.data?.message || 'Guests deleted');
      setShowBulkDeleteDialog(false);
      setBulkDeleteMode('selected');
      setBulkDeleteConfirmation('');
      setSelectedBulkGuestIds([]);
      setSelectedGuest(null);
      await fetchGuests();
      checkWhatsAppReadiness();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete guests');
    } finally {
      setDeletingAllGuests(false);
    }
  };

  const handleGroupSelectedGuests = async () => {
    if (!currentEvent?.id || selectedBulkGuestIds.length < 2) return;
    setGroupingGuests(true);
    try {
      const response = await api.post(`/events/${currentEvent.id}/guests/display-group`, {
        guest_ids: selectedBulkGuestIds,
      });
      toast.success(response.data?.message || 'Guests grouped');
      setSelectedBulkGuestIds([]);
      await fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to group guests');
    } finally {
      setGroupingGuests(false);
    }
  };

  const handleEditGuest = async (guest) => {
    const buildEditForm = (source, group) => {
      const namedGuests = group
        .filter((item) => !isGeneratedAccompanyingGuest(item))
        .sort((a, b) => {
          if (a.is_primary_in_phone_group !== b.is_primary_in_phone_group) {
            return a.is_primary_in_phone_group ? -1 : 1;
          }
          if (a.id === source.id) return -1;
          if (b.id === source.id) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
      const nameSlots = buildGuestNameSlots(
        (namedGuests.length ? namedGuests : [source]).map((item) => ({
          id: item.id,
          name: item.name || '',
        })),
        getGuestGroupAccompanyingCount(group),
      );

      return {
        id: guest.id,
        phone: source.phone || '',
        email: source.email || '',
        guest_side: normalizeGuestSide(source.guest_side),
        requires_room: group.some((item) => item.requires_room !== false),
        rooms_required: source.rooms_required ?? '',
        companion_count: getCompanionSlotCount(nameSlots),
        dietary_restrictions: source.dietary_restrictions || '',
        tagged_itinerary_event_ids: source.tagged_itinerary_event_ids || [],
        names: nameSlots,
      };
    };

    const fallbackGroup = guests.filter((item) => item.phone && item.phone === guest.phone);
    const seedGroup = fallbackGroup.length ? fallbackGroup : [guest];
    setEditingGuest(buildEditForm(guest, seedGroup));
    setShowEditDialog(true);

    try {
      const response = await api.get(`/events/${currentEvent.id}/guests`, {
        params: { search: guest.phone, limit: 200 }
      });
      const phoneGroup = (response.data || []).filter((item) => item.phone === guest.phone);
      if (phoneGroup.length > 0) {
        const sharedSource = phoneGroup.find((item) => item.id === guest.id) || phoneGroup[0];
        setEditingGuest(buildEditForm(sharedSource, phoneGroup));
      }
    } catch (error) {
      toast.error('Loaded current row only; full phone group could not be refreshed');
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const basePayload = normalizeGuestGroupForm(editingGuest);
    if (!basePayload) return;
    const payload = basePayload;

    try {
      const response = await api.put(`/events/${currentEvent.id}/guests/group/${editingGuest.id}`, payload);
      toast.success(response.data?.message || 'Guest group updated');
      setShowEditDialog(false);
      setEditingGuest(null);
      fetchGuests();
      checkWhatsAppReadiness();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update guest'));
    }
  };

  const viewGuestProfile = async (guest) => {
    setSelectedGuest(guest);
    setShowGuestDialog(true);
    try {
      const response = await api.get(`/events/${currentEvent.id}/guests/${guest.id}/details`);
      setSelectedGuest({
        ...(response.data?.guest || guest),
        phone_group: response.data?.phone_group || [],
        party_summary: response.data?.party_summary || null,
        companion_guests: response.data?.companion_guests || [],
        assigned_itinerary_events: response.data?.assigned_itinerary_events || []
      });
    } catch (error) {
      try {
        const fallback = await api.get(`/events/${currentEvent.id}/guests/${guest.id}`);
        setSelectedGuest(fallback.data);
      } catch (_fallbackError) {
        toast.error('Failed to load guest profile');
      }
    }
  };

  // WhatsApp functions
  const openWhatsAppDialog = () => {
    checkWhatsAppReadiness();
    fetchInvitePacks();
    setSelectedGuestIds([]);
    setSelectedInvitePackId('__auto__');
    setWhatsappMode('invite');
    setShowWhatsAppDialog(true);
  };

  const isWhatsAppEligible = (guest) => !!guest.phone;

  const handleSelectGuest = (guestId, checked) => {
    if (checked) {
      setSelectedGuestIds([...selectedGuestIds, guestId]);
    } else {
      setSelectedGuestIds(selectedGuestIds.filter(id => id !== guestId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const validGuests = guests.filter(isWhatsAppEligible);
      setSelectedGuestIds(validGuests.map(g => g.id));
    } else {
      setSelectedGuestIds([]);
    }
  };

  const sendWhatsAppInvitations = async (sendToAll = false) => {
    setSendingWhatsApp(true);
    try {
      const modeGuests = Array.isArray(currentWhatsAppMode?.guests) ? currentWhatsAppMode.guests : [];
      const eligibleGuestIds = new Set(
        modeGuests
          .filter(isWhatsAppEligible)
          .map((guest) => guest.id)
          .filter(Boolean)
      );
      const selectedIdsForMode = selectedGuestIds.filter((id) => eligibleGuestIds.has(id));
      if (!sendToAll && selectedIdsForMode.length === 0) {
        toast.error('Please select at least one guest');
        return;
      }

      const formData = new FormData();
      formData.append('send_to_all', sendToAll);
      if (selectedInvitePackId && selectedInvitePackId !== '__auto__') {
        formData.append('invite_pack_id', selectedInvitePackId);
      }
      if (!sendToAll) {
        selectedIdsForMode.forEach((guestId) => {
          formData.append('guest_ids', guestId);
        });
      }

      const response = await api.post(
        `/events/${currentEvent.id}/whatsapp/send`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const summary = response.data?.summary || {};
      toast.success(`Invitations sent: ${summary.sent || 0} sent, ${summary.failed || 0} failed, ${summary.skipped || 0} skipped`);
      
      setShowWhatsAppDialog(false);
      fetchGuests();
      checkWhatsAppReadiness();
    } catch (error) {
      console.error('Failed to send WhatsApp invitations:', error);
      toast.error(getApiErrorMessage(error, 'Failed to send invitations'));
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const sendSingleInvitation = async (guestId) => {
    setSendingWhatsApp(true);
    try {
      const response = await api.post(`/events/${currentEvent.id}/whatsapp/send/${guestId}`);
      const summary = response.data?.summary || {};
      if ((summary.sent || 0) > 0) {
        toast.success('Invitation sent');
      } else {
        toast.error(response.data?.results?.failed?.[0]?.error || 'WhatsApp did not accept this invitation');
      }
      fetchGuests();
      checkWhatsAppReadiness();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send invitation'));
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const sendReminder = async (reminderType, sendToAll = false) => {
    setSendingWhatsApp(true);
    try {
      const payload = new FormData();
      payload.append('reminder_type', reminderType);
      if (!sendToAll && visibleSelectedGuestIds.length > 0) {
        visibleSelectedGuestIds.forEach((guestId) => {
          payload.append('guest_ids', guestId);
        });
      }

      const response = await api.post(
        `/events/${currentEvent.id}/whatsapp/send-reminder`,
        payload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const summary = response.data?.summary || {};
      const labels = {
        rsvp: 'RSVP reminders',
        id_upload: 'ID reminders',
        travel_upload: 'Travel reminders'
      };
      toast.success(`${labels[reminderType] || 'Notifications'} sent: ${summary.sent || 0} sent, ${summary.failed || 0} failed, ${summary.skipped || 0} skipped`);
      fetchGuests();
      checkWhatsAppReadiness();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send reminder'));
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const confirmWhatsAppSend = () => {
    const pendingSend = pendingWhatsAppSend;
    if (!pendingSend) return;
    setPendingWhatsAppSend(null);
    if (pendingSend.closeGuestDialog) setShowGuestDialog(false);

    if (pendingSend.kind === 'single_invite') {
      sendSingleInvitation(pendingSend.guestId);
    } else if (pendingSend.kind === 'invite') {
      sendWhatsAppInvitations(pendingSend.sendToAll);
    } else if (pendingSend.kind === 'reminder') {
      sendReminder(pendingSend.reminderType, pendingSend.sendToAll);
    }
  };

  const getRSVPBadge = (status) => {
    const variants = {
      confirmed: { color: 'bg-green-100 text-green-700', icon: Check },
      declined: { color: 'bg-red-100 text-red-700', icon: X },
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock }
    };
    const variant = variants[status] || variants.pending;
    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        <variant.icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getGuestEventRsvpStatus = (guest, itineraryItemId) => {
    const response = eventRsvps.find((rsvp) => (
      rsvp.guest_id === guest?.id && rsvp.itinerary_item_id === itineraryItemId
    ));
    return response?.status || 'pending';
  };

  const getWhatsAppStatusBadge = (guest) => {
    const status = guest.whatsapp_invite_status || 'pending';

    const variants = {
      pending: { color: 'bg-gray-100 text-gray-600', icon: Clock, label: 'Not Sent' },
      accepted: { color: 'bg-blue-100 text-blue-700', icon: PaperPlaneTilt, label: 'Sent' },
      sent: { color: 'bg-blue-100 text-blue-700', icon: PaperPlaneTilt, label: 'In Transit' },
      delivered: { color: 'bg-purple-100 text-purple-700', icon: Check, label: 'Delivered' },
      read: { color: 'bg-indigo-100 text-indigo-700', icon: Eye, label: 'Read' },
      failed: { color: 'bg-red-100 text-red-700', icon: X, label: 'Failed' }
    };
    
    const variant = variants[status] || variants.pending;
    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        <variant.icon className="w-3 h-3" />
        {variant.label}
      </Badge>
    );
  };

  const renderWhatsAppInviteAction = (guest) => {
    if (!isWhatsAppEligible(guest)) return null;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-green-600 hover:bg-green-50 hover:text-green-700"
            onClick={(event) => {
              event.stopPropagation();
              setPendingWhatsAppSend({
                kind: 'single_invite',
                guestId: guest.id,
                guestName: guest.name,
                recipientCount: 1,
              });
            }}
            aria-label={`Send WhatsApp invite to ${guest.name}`}
            data-testid={`send-whatsapp-invite-${guest.id}`}
          >
            <WhatsappLogo className="h-4 w-4" weight="fill" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Send WhatsApp invite</TooltipContent>
      </Tooltip>
    );
  };

  const hasIdUpload = (guest) => guest.has_id_upload ?? (guest.id_info?.id_uploads || []).length > 0;
  const hasArrivalUpload = (guest) => guest.has_arrival_upload ?? (guest.arrival_info?.arrival_tickets || []).length > 0;
  const hasDepartureUpload = (guest) => guest.has_departure_upload ?? (guest.arrival_info?.departure_tickets || []).length > 0;

  const matchesUploadFilter = (value, hasUpload) => (
    value === 'all' || (value === 'uploaded' ? hasUpload : !hasUpload)
  );

  const getGuestSideLabel = (side) => {
    const normalizedSide = normalizeGuestSide(side);
    if (normalizedSide === 'bride') return 'Bride side';
    if (normalizedSide === 'groom') return 'Groom side';
    return 'Both sides';
  };

  const getGuestSideBadge = (side) => {
    const normalizedSide = normalizeGuestSide(side);
    if (normalizedSide === 'bride') {
      return <Badge className="bg-rose-100 text-rose-700">Bride</Badge>;
    }
    if (normalizedSide === 'groom') {
      return <Badge className="bg-sky-100 text-sky-700">Groom</Badge>;
    }
    return <Badge variant="outline">Both sides</Badge>;
  };

  const getGuestRoomAllocationState = (guest) => (
    guest?.booking_allocation_state
    || guest?.room_allocation_state
    || ((guest?.room_allocated ?? !!guest?.room_id) ? 'allocated' : 'unallocated')
  );

  const isGuestRoomAllocated = (guest) => getGuestRoomAllocationState(guest) === 'allocated';
  const guestRequiresRoom = (guest) => guest?.requires_room !== false;

  const getGuestRoomPendingCount = (guest) => {
    const partySize = Number(guest?.booking_party_size || 0);
    const allocatedBeds = Number(guest?.booking_allocated_beds || 0);
    if (!partySize || allocatedBeds >= partySize) return 0;
    return partySize - allocatedBeds;
  };

  const getGuestRoomStatusLabel = (guest) => {
    if (!guestRequiresRoom(guest)) return 'Room not required';
    if (guest?.rsvp_status === 'declined') return 'Room not required - RSVP declined';
    if (getGuestRoomAllocationState(guest) === 'partial') {
      const pendingCount = getGuestRoomPendingCount(guest);
      return pendingCount > 0
        ? `${pendingCount} ${pendingCount === 1 ? 'guest' : 'guests'} pending room allocation`
        : guest.room_allocation_label || 'Room allocation is incomplete';
    }
    if (!isGuestRoomAllocated(guest)) {
      return guest.room_allocation_label || 'Room allocation pending';
    }
    return `Room allocated: ${guest.room_allocation_label || 'Assigned room'}`;
  };

  const getStatusIconTone = ({ active, tone, state }) => {
    if (state && GUEST_STATUS_VISUALS[state]) {
      return GUEST_STATUS_VISUALS[state].container;
    }
    if (!active) {
      return 'border-slate-200 bg-slate-100 text-slate-400';
    }

    const tones = {
      green: 'border-green-200 bg-green-100 text-green-700',
      red: 'border-red-200 bg-red-100 text-red-700',
      yellow: 'border-amber-200 bg-amber-100 text-amber-700',
      rose: 'border-rose-200 bg-rose-100 text-rose-700',
      sky: 'border-sky-200 bg-sky-100 text-sky-700'
    };

    return tones[tone] || tones.green;
  };

  const renderStatusIcon = ({
    icon: Icon,
    label,
    active = true,
    tone = 'green',
    text,
    state,
    badgeText,
    showStateMarker = true
  }) => {
    const stateVisual = showStateMarker ? GUEST_STATUS_VISUALS[state] : null;
    const StateIcon = stateVisual?.icon;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'relative flex h-7 shrink-0 items-center justify-center rounded-full border transition-colors',
              badgeText ? 'w-auto min-w-[4.25rem] gap-1 px-2' : 'w-7',
              getStatusIconTone({ active, tone, state })
            )}
            role="img"
            aria-label={`${stateVisual?.label ? `${stateVisual.label}: ` : ''}${label}`}
          >
            {text ? (
              <span className="text-[0.6875rem] font-semibold uppercase">{text}</span>
            ) : (
              <Icon
                className="h-3.5 w-3.5"
                weight={state ? 'regular' : active ? 'fill' : 'regular'}
              />
            )}
            {badgeText && (
              <span className="text-[0.625rem] font-semibold normal-case leading-none">
                {badgeText}
              </span>
            )}
            {StateIcon && (
              <span
                className={cn(
                  'absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full',
                  stateVisual.marker
                )}
                aria-hidden="true"
              >
                <StateIcon className="h-1.5 w-1.5" weight="bold" />
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            <p className="font-medium">{label}</p>
            {stateVisual && <p className="text-xs opacity-80">{stateVisual.label}</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  const renderGuestSideStatus = (guest) => {
    const side = normalizeGuestSide(guest?.guest_side);
    return renderStatusIcon({
      icon: side === 'bride' ? Heart : UsersThree,
      label: getGuestSideLabel(side),
      active: true,
      tone: side === 'bride' ? 'rose' : side === 'groom' ? 'sky' : 'yellow',
      text: side === 'both' ? null : side.charAt(0).toUpperCase(),
      showStateMarker: false
    });
  };

  const renderItineraryTagSelector = (form, setter, prefix, onSelectionChange) => (
    <CollapsibleItinerarySelector
      items={sortedItineraryItems}
      selectedIds={form.tagged_itinerary_event_ids || []}
      onChange={(itineraryEventIds) => {
        onSelectionChange?.();
        setter((current) => ({
          ...current,
          tagged_itinerary_event_ids: itineraryEventIds
        }));
      }}
      title="Itinerary Events"
      emptyMessage="Add itinerary events first to tag guests to specific moments."
      idPrefix={`${prefix}-itinerary`}
      defaultSelectAll={prefix === 'guest'}
      renderPrimaryLabel={getItineraryItemLabel}
    />
  );

  const renderGuestGroupFields = (form, setter, prefix, onItinerarySelectionChange) => (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor={`${prefix}-phone`}>Phone (with country code) *</Label>
          <Input
            id={`${prefix}-phone`}
            value={form.phone}
            onChange={(e) => setter((current) => ({ ...current, phone: e.target.value }))}
            placeholder="+91 98765 43210"
            required
            data-testid={prefix === 'guest' ? 'guest-phone-input' : 'edit-guest-phone-input'}
          />
          <p className="text-xs text-muted-foreground mt-1">Guests are matched to accounts by phone number.</p>
        </div>
        <div>
          <Label htmlFor={`${prefix}-email`}>Email (optional)</Label>
          <Input
            id={`${prefix}-email`}
            type="email"
            value={form.email}
            onChange={(e) => setter((current) => ({ ...current, email: e.target.value }))}
            data-testid={prefix === 'guest' ? 'guest-email-input' : 'edit-guest-email-input'}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {eventTypeConfig.features.showGuestSides && <div>
          <Label>Guest Side</Label>
          <Select
            value={normalizeGuestSide(form.guest_side)}
            onValueChange={(value) => setter((current) => ({ ...current, guest_side: value }))}
          >
            <SelectTrigger data-testid={prefix === 'guest' ? 'guest-side-select' : 'edit-guest-side-select'}>
              <SelectValue placeholder="Select guest side" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="both">Both sides</SelectItem>
              <SelectItem value="bride">Bride side</SelectItem>
              <SelectItem value="groom">Groom side</SelectItem>
            </SelectContent>
          </Select>
        </div>}
        <div>
          <Label htmlFor={`${prefix}-dietary`}>Dietary Restrictions</Label>
          <Input
            id={`${prefix}-dietary`}
            value={form.dietary_restrictions}
            onChange={(e) => setter((current) => ({ ...current, dietary_restrictions: e.target.value }))}
            data-testid={prefix === 'guest' ? 'guest-dietary-input' : 'edit-guest-dietary-input'}
          />
        </div>
      </div>
      {renderItineraryTagSelector(form, setter, prefix, onItinerarySelectionChange)}
    </>
  );

  const renderGuestNameRows = (form, setter, prefix) => {
    const accompanyingCount = getCompanionSlotCount(form.names || []);
    const minimumAccompanyingCount = getNamedCompanionCount(form.names || []);
    const setAccompanyingCount = (value) => {
      setter((current) => {
        const nextNames = resizeCompanionSlots(current.names || [], value);
        return {
          ...current,
          names: nextNames,
          companion_count: getCompanionSlotCount(nextNames),
        };
      });
    };

    return (
    <div className="space-y-2">
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <Label className="self-start">Guest Names *</Label>
        <div className="grid w-full gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-end">
          {eventTypeConfig.features.showAccommodation && <div className={cn(
            'flex min-h-9 w-full items-center gap-2 rounded-md border px-3 transition-colors sm:w-auto',
            form.requires_room
              ? 'border-[#B7D6C8] bg-[#F1F8F4] text-[#174C3A]'
              : 'border-[#E4E7E5] bg-[#F8F9F8] text-[#66716C]'
          )}>
            <Label
              htmlFor={`${prefix}-group-room`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs font-semibold"
            >
              <Bed className="h-4 w-4" weight={form.requires_room ? 'fill' : 'regular'} />
              Needs room
            </Label>
            <Switch
              id={`${prefix}-group-room`}
              checked={form.requires_room === true}
              onCheckedChange={(requires_room) => setter((current) => ({
                ...current,
                requires_room,
                rooms_required: requires_room ? current.rooms_required : '',
              }))}
              aria-label="Room required for guest group"
              data-testid={`${prefix}-group-room-toggle`}
            />
            {form.requires_room === true && (
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={50}
                step={1}
                value={form.rooms_required ?? ''}
                onChange={(event) => setter((current) => ({
                  ...current,
                  rooms_required: event.target.value,
                }))}
                placeholder="How many"
                aria-label="Rooms required"
                className="ml-auto h-7 min-w-0 w-[5.25rem] bg-white px-2 text-xs shadow-none placeholder:text-[0.625rem]"
                data-testid={`${prefix}-rooms-required`}
              />
            )}
          </div>}
          <div className="flex h-9 w-full items-center overflow-hidden rounded-md border bg-background sm:w-auto" title="Total guests accompanying the first invitee">
            <span className="flex min-w-0 flex-1 items-center gap-1.5 px-2 text-xs font-semibold text-muted-foreground sm:flex-none">
              <UsersThree className="h-4 w-4" />
              Accompanying
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-none border-l"
              onClick={() => setAccompanyingCount(accompanyingCount - 1)}
              disabled={accompanyingCount <= minimumAccompanyingCount}
              aria-label="Decrease accompanying guest count"
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <Input
              type="number"
              min={minimumAccompanyingCount}
              max={50}
              value={accompanyingCount}
              onChange={(event) => setAccompanyingCount(event.target.value)}
              className="h-8 w-12 rounded-none border-0 border-l px-1 text-center shadow-none focus-visible:ring-0"
              aria-label="Accompanying guest count"
              data-testid={`${prefix}-accompanying-count`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-none border-l"
              onClick={() => setAccompanyingCount(accompanyingCount + 1)}
              disabled={accompanyingCount >= 50}
              aria-label="Increase accompanying guest count"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {(form.names || []).map((row, index) => {
          const value = typeof row === 'string' ? row : row.name;
          return (
            <div
              key={typeof row === 'object' && row.id ? row.id : index}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2"
            >
              <Input
                value={value || ''}
                onChange={(e) => updateGuestNameRow(setter, index, e.target.value)}
                placeholder={index === 0 ? 'Full name' : 'Enter companion name or leave blank'}
                required={index === 0}
                data-testid={prefix === 'guest' && index === 0 ? 'guest-name-input' : prefix === 'edit' && index === 0 ? 'edit-guest-name-input' : `${prefix}-guest-name-${index}`}
              />
              {index === 0 ? (
                <span className="hidden h-9 w-9 sm:block" aria-hidden="true" />
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => removeGuestNameRow(setter, index)}
                  aria-label="Remove companion"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
    );
  };

  const filteredGuests = guests.filter((guest) => !isGeneratedAccompanyingGuest(guest)).filter((guest) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = !query || [guest.name, guest.email, guest.phone]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(query));

    const matchesRsvp = filters.rsvp === 'all' || guest.rsvp_status === filters.rsvp;
    const matchesId = !eventTypeConfig.features.showTravelAndId
      || matchesUploadFilter(filters.idUpload, hasIdUpload(guest));
    const matchesArrival = !eventTypeConfig.features.showTravelAndId
      || matchesUploadFilter(filters.arrivalUpload, hasArrivalUpload(guest));
    const matchesDeparture = !eventTypeConfig.features.showTravelAndId
      || matchesUploadFilter(filters.departureUpload, hasDepartureUpload(guest));
    const matchesSide = !eventTypeConfig.features.showGuestSides
      || filters.side === 'all'
      || guest.guest_side === filters.side;
    const matchesItinerary = !(filters.itineraryEventIds || []).length ||
      filters.itineraryEventIds.every((itemId) => (guest.tagged_itinerary_event_ids || []).includes(itemId));

    return matchesSearch && matchesRsvp && matchesId && matchesArrival && matchesDeparture && matchesSide && matchesItinerary;
  });

  /**
   * Group guests by phone number. Until RSVP chooses a primary identity,
   * the first guest in the phone group acts as the display primary.
   */
  const displayGuests = React.useMemo(() => {
    const byPhone = new Map();
    filteredGuests.forEach((g) => {
      const key = (g.phone || '').trim() || `__no_phone_${g.id}`;
      if (!byPhone.has(key)) byPhone.set(key, []);
      byPhone.get(key).push(g);
    });

    const rows = [];
    const renderedPhoneGroups = new Set();
    filteredGuests.forEach((guest) => {
      const key = (guest.phone || '').trim() || `__no_phone_${guest.id}`;
      const group = byPhone.get(key) || [];
      if (renderedPhoneGroups.has(key)) return;
      renderedPhoneGroups.add(key);
      const primaryGuest = group.find((g) => g.is_primary_in_phone_group) || group[0] || guest;
      const groupAccompanyingCount = getGuestGroupAccompanyingCount(group);

      const siblings = group.filter((g) => g.id !== primaryGuest.id);

      rows.push({
        guest: primaryGuest,
        indent: false,
        groupSize: group.length,
        isPrimary: true,
        accompanyingCount: groupAccompanyingCount,
      });
      siblings.forEach((s) => {
        rows.push({ guest: s, indent: true, groupSize: group.length, isPrimary: false });
      });
    });
    const visualGroupIds = rows.map((row) => {
      // Shared-phone secondary contacts stay outside the purple display-group
      // treatment. Their primary contact acts as the sole visual representative.
      if (row.groupSize > 1 && !row.isPrimary) return null;
      if (row.guest.display_group_id) return row.guest.display_group_id;
      if (row.groupSize < 2) return null;

      const phoneKey = (row.guest.phone || '').trim();
      if (!phoneKey) return null;

      // A shared-number primary remains a visual anchor when one of its
      // accompanying guests has been placed in a display group. This does not
      // add the primary guest to the saved group or bulk selection.
      return (byPhone.get(phoneKey) || []).find((item) => item.display_group_id)?.display_group_id || null;
    });

    return rows.map((row, index) => {
      const groupId = visualGroupIds[index];
      if (!groupId) return { ...row, displayGroupPosition: null };
      const previousGroupId = visualGroupIds[index - 1];
      const nextGroupId = visualGroupIds[index + 1];
      return {
        ...row,
        visualDisplayGroupId: groupId,
        displayGroupPosition: previousGroupId !== groupId
          ? 'first'
          : nextGroupId !== groupId
            ? 'last'
            : 'middle',
      };
    });
  }, [filteredGuests]);

  const visibleGuestIds = displayGuests.map(({ guest }) => guest.id);
  const selectedVisibleGuestIds = visibleGuestIds.filter((guestId) => selectedBulkGuestIds.includes(guestId));
  const allVisibleGuestsSelected = visibleGuestIds.length > 0 && selectedVisibleGuestIds.length === visibleGuestIds.length;
  const someVisibleGuestsSelected = selectedVisibleGuestIds.length > 0 && !allVisibleGuestsSelected;

  const handleToggleBulkGuest = (guestId, checked) => {
    setSelectedBulkGuestIds((currentIds) => {
      if (checked) {
        return currentIds.includes(guestId) ? currentIds : [...currentIds, guestId];
      }
      return currentIds.filter((id) => id !== guestId);
    });
  };

  const handleToggleAllVisibleGuests = (checked) => {
    setSelectedBulkGuestIds((currentIds) => {
      if (!checked) {
        return currentIds.filter((guestId) => !visibleGuestIds.includes(guestId));
      }

      const nextIds = [...currentIds];
      visibleGuestIds.forEach((guestId) => {
        if (!nextIds.includes(guestId)) nextIds.push(guestId);
      });
      return nextIds;
    });
  };

  const pendingRsvpGuests = filteredGuests.filter((guest) => guest.rsvp_status === 'pending');
  const pendingIdGuests = filteredGuests.filter((guest) => !hasIdUpload(guest));
  const pendingTravelGuests = filteredGuests.filter((guest) => !hasArrivalUpload(guest) || !hasDepartureUpload(guest));

  const whatsappModeConfig = {
    invite: {
      label: 'Invite',
      description: `${eventTypeConfig.label} invitation message for selected guests.`,
      guests: guests.filter(isWhatsAppEligible),
      sendAllLabel: `Send to All (${guests.filter(isWhatsAppEligible).length})`,
      sendSelectedLabel: `Send to Selected (${selectedGuestIds.length})`,
      selectionHelper: 'Select guests to send the main invitation.'
    },
    rsvp: {
      label: 'RSVP Reminder',
      description: 'Guests who have not RSVP’d yet.',
      guests: pendingRsvpGuests,
      sendAllLabel: `Send RSVP Reminder to All (${pendingRsvpGuests.length})`,
      sendSelectedLabel: `Send RSVP Reminder to Selected (${selectedGuestIds.length})`,
      selectionHelper: 'These are the guests with pending RSVPs.'
    },
    id_upload: {
      label: 'ID Reminder',
      description: 'Guests who have not uploaded their ID yet.',
      guests: pendingIdGuests,
      sendAllLabel: `Send ID Reminder to All (${pendingIdGuests.length})`,
      sendSelectedLabel: `Send ID Reminder to Selected (${selectedGuestIds.length})`,
      selectionHelper: 'These guests still need to upload an ID.'
    },
    travel_upload: {
      label: 'Travel Reminder',
      description: 'Guests missing arrival or departure uploads.',
      guests: pendingTravelGuests,
      sendAllLabel: `Send Travel Reminder to All (${pendingTravelGuests.length})`,
      sendSelectedLabel: `Send Travel Reminder to Selected (${selectedGuestIds.length})`,
      selectionHelper: 'These guests still need travel details.'
    }
  };

  const currentWhatsAppMode = whatsappModeConfig[whatsappMode] || whatsappModeConfig.invite;
  const visibleWhatsAppGuests = Array.isArray(currentWhatsAppMode.guests) ? currentWhatsAppMode.guests : [];
  const visibleWhatsAppGuestIds = visibleWhatsAppGuests.map((guest) => guest.id);
  const visibleSelectedGuestIds = selectedGuestIds.filter((id) => visibleWhatsAppGuestIds.includes(id));
  const itineraryNameById = React.useMemo(() => {
    const map = new Map();
    sortedItineraryItems.forEach((item) => {
      if (item?.id) map.set(item.id, item.name || 'Itinerary event');
    });
    return map;
  }, [sortedItineraryItems]);
  const inviteMediaOptions = React.useMemo(() => (
    invitePacks.filter((item) => item?.media_url && !item?.is_deleted)
  ), [invitePacks]);
  const selectedInvitePack = inviteMediaOptions.find((item) => item.id === selectedInvitePackId);
  const invitePackLabel = (item) => {
    const side = eventTypeConfig.features.showGuestSides
      ? ({
        default: 'Default',
        bride: 'Bride side',
        groom: 'Groom side',
        both: 'Both sides',
      }[item?.audience_side || 'default'] || 'Default')
      : 'All guests';
    let scope = eventTypeConfig.labels.fullEventInvite;
    if (item?.invite_scope === 'event') {
      scope = itineraryNameById.get(item.itinerary_event_id) || 'Itinerary event';
    } else if (item?.invite_scope === 'selected_events') {
      const names = (item.itinerary_event_ids || [])
        .map((id) => itineraryNameById.get(id))
        .filter(Boolean);
      scope = names.length ? names.join(' + ') : 'Selected events';
    }
    return `${item?.title || scope} - ${side}`;
  };

  const guestsWithoutPhone = whatsappReadiness?.missing_phones || [];
  const themeMissing = whatsappReadiness?.theme_missing;
  const stats = whatsappReadiness?.invitation_stats || {};

  const resolveUploadedFileUrl = (url) => (
    url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url
  );

  const isPdfFile = (url) => url?.toLowerCase().split('?')[0].endsWith('.pdf');

  const openIdPreview = (url, label) => {
    setIdPreviewDialog({
      url: resolveUploadedFileUrl(url),
      label
    });
  };

  const getIdUploads = (idInfo) => (
    (idInfo?.id_uploads || []).map((upload, index) => {
      if (typeof upload === 'string') {
        return {
          url: upload,
          id_type: idInfo?.id_type || 'ID Proof',
          key: `${upload}-${index}`
        };
      }

      return {
        url: upload.url,
        id_type: upload.id_type || idInfo?.id_type || 'ID Proof',
        key: `${upload.url}-${index}`
      };
    })
  );

  const getGuestInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'G';
    return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  };

  const formatProfileDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTicketPassengerNames = (tickets = []) => {
    const names = new Set();
    tickets.forEach((ticket) => {
      ticket.extracted_data?.passenger_names?.forEach((name) => {
        if (name) names.add(name);
      });
    });
    return Array.from(names);
  };

  const getTicketCompanionNames = (tickets = []) => {
    const ids = new Set();
    tickets.forEach((ticket) => {
      ticket.accompanying_guest_ids?.forEach((id) => {
        if (id) ids.add(id);
      });
    });
    return Array.from(ids).map((guestId) => guests.find((guest) => guest.id === guestId)?.name || guestId);
  };

  const renderDetailLine = (label, value) => (
    <div className="min-w-0">
      <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[#958A80]">{label}</p>
      <p className="mt-1 truncate text-sm font-medium text-[#2F2A26]">{value || 'Not provided'}</p>
    </div>
  );

  const renderTravelCard = (guest, type) => {
    const isArrival = type === 'arrival';
    const info = guest.arrival_info || {};
    const tickets = isArrival ? info.arrival_tickets || [] : info.departure_tickets || [];
    const hasDate = isArrival ? info.arrival_date : info.departure_date;
    const mode = isArrival ? info.arrival_mode : info.departure_mode;
    const time = isArrival ? info.arrival_time : info.departure_time;
    const details = isArrival ? info.arrival_details : info.departure_details;
    const Icon = isArrival ? AirplaneLanding : AirplaneTakeoff;
    const passengers = getTicketPassengerNames(tickets);
    const companions = getTicketCompanionNames(tickets);

    return (
      <div className={cn(
        'rounded-xl border p-3',
        isArrival ? 'border-[#CFE8D7] bg-[#F4FBF6]' : 'border-[#F1D4CF] bg-[#FFF7F5]'
      )}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              isArrival ? 'bg-[#DDEFE3] text-[#4F7358]' : 'bg-[#F6DEDA] text-[#9A5A51]'
            )}>
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#2F2A26]">{isArrival ? 'Arrival' : 'Departure'}</p>
              <p className="truncate text-xs text-[#7A7168]">
                {hasDate
                  ? [mode, formatProfileDate(hasDate), time].filter(Boolean).join(' - ')
                  : 'Travel details pending'}
              </p>
            </div>
          </div>
          {hasAdminAccess && (
            <>
              <AIPremiumButton
                featureName="ticket reading"
                type="button"
                variant="outline"
                size="sm"
                onManualEntry={() => setManualTravelEntry({ guest, ticketType: type })}
                className="h-8 shrink-0 border-[#DED2C4] bg-white px-2.5 text-xs"
              >
                Ticket
              </AIPremiumButton>
            </>
          )}
        </div>
        {details && <p className="mt-2 text-xs text-[#6E665F]">{details}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline" className="border-white bg-white text-[#5F574F]">
            {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
          </Badge>
          {passengers.slice(0, 2).map((name) => (
            <Badge key={name} variant="outline" className="border-white bg-white text-[#5F574F]">
              {name}
            </Badge>
          ))}
          {passengers.length > 2 && (
            <Badge variant="outline" className="border-white bg-white text-[#5F574F]">
              +{passengers.length - 2} passengers
            </Badge>
          )}
          {companions.length > 0 && (
            <Badge variant="outline" className="border-white bg-white text-[#5F574F]">
              {companions.length} accompanying
            </Badge>
          )}
        </div>
      </div>
    );
  };

  const openStatusFieldsDialog = () => {
    setDraftStatusFields(visibleGuestStatusOptions
      .map((option) => option.id)
      .filter((fieldId) => enabledStatusFields.has(fieldId)));
    setShowStatusFieldsDialog(true);
  };

  const toggleStatusField = (fieldId, enabled) => {
    setDraftStatusFields((current) => (
      enabled
        ? [...current.filter((item) => item !== fieldId), fieldId]
        : current.filter((item) => item !== fieldId)
    ));
  };

  const saveStatusFields = async () => {
    if (!currentEvent?.id) return;
    try {
      setSavingStatusFields(true);
      await api.put(`/events/${currentEvent.id}/guest-status-fields`, {
        enabled_fields: visibleGuestStatusOptions
          .map((option) => option.id)
          .filter((fieldId) => draftStatusFields.includes(fieldId))
      });
      await refreshCurrentEvent();
      setShowStatusFieldsDialog(false);
      toast.success('Guest status fields updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update guest status fields');
    } finally {
      setSavingStatusFields(false);
    }
  };

  const handleUploadGuestIdProof = async (event, guest) => {
    const file = event.target.files?.[0];
    if (!file || !guest?.id || !currentEvent?.id) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('id_type', guest.id_info?.id_type || 'ID Proof');

    try {
      setUploadingGuestId(true);
      await api.post(
        `/events/${currentEvent.id}/guests/${guest.id}/id-proof`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('ID proof uploaded');
      await fetchGuests();
      await viewGuestProfile(guest);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload ID proof');
    } finally {
      setUploadingGuestId(false);
      event.target.value = '';
    }
  };

  const activeFilterChips = getActiveFilterChips();

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <StickyActionHeader className="gap-2">
          <div>
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="guests-title">
              Guest Management
            </h1>
            <p className="text-muted-foreground">
              {getGuestPartyCount(guests)} of {guestPartyTotal || getGuestPartyCount(guests)} guests shown
            </p>
          </div>
          <div className="flex w-full items-center gap-1.5 sm:w-auto sm:gap-2" data-header-action-group>
            <Button 
              variant="outline" 
              className="border-green-200 px-3 text-green-600 hover:bg-green-50"
              onClick={openWhatsAppDialog}
              aria-label="Send notifications"
              title="Send notifications"
              data-header-action
              data-testid="whatsapp-invite-btn"
            >
              <WhatsappLogo className="mr-2 h-4 w-4" weight="fill" />
              <span>Notify</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              className="hidden xl:inline-flex"
              onClick={handleOpenPhoneContacts}
              disabled={importingPhoneContacts || loadingPhoneContacts}
              aria-label="Import phone contacts"
              title={isPhoneContactPickerSupported()
                ? 'Select contacts from this Android phone'
                : 'Import contacts from a vCard file'}
              data-header-action
              data-testid="import-phone-contacts-btn"
            >
              {importingPhoneContacts || loadingPhoneContacts ? (
                <SpinnerGap className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <AddressBook className="mr-2 h-4 w-4" />
              )}
              {importingPhoneContacts || loadingPhoneContacts
                ? 'Loading Contacts...'
                : phoneContacts.length > 0
                  ? `Contacts (${phoneContacts.length})`
                  : isPhoneContactPickerSupported() ? 'Phone Contacts' : 'Import Contacts'}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="hidden xl:inline-flex"
              onClick={handleExportGuests}
              disabled={exportingGuests}
              aria-label="Export guest list"
              title="Export guest list"
              data-header-action
              data-testid="export-guests-btn"
            >
              {exportingGuests ? (
                <SpinnerGap className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <DownloadSimple className="mr-2 h-4 w-4" />
              )}
              {exportingGuests ? 'Exporting...' : 'Export'}
            </Button>

            <Dialog open={showContactFileDialog} onOpenChange={setShowContactFileDialog}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Import contacts from this device</DialogTitle>
                  <DialogDescription>
                    iPhone, Safari, and desktop browsers cannot directly open the system contact picker. Export the contacts you want as a vCard file, then choose that file here.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <ol className="list-decimal space-y-2 pl-5">
                    <li>On iPhone, open Contacts, tap Lists, touch and hold a list, then tap Export.</li>
                    <li>Save or share the exported <span className="font-medium text-foreground">.vcf</span> file to Files.</li>
                    <li>Choose one or more files below, then select which contacts should become guests.</li>
                  </ol>
                  <p className="rounded-md border bg-muted/30 p-3 text-xs leading-5">
                    You can combine vCard exports from multiple phones. Loaded contacts are saved with this event and remain available until the event is removed; none become guests until you select them and confirm.
                  </p>
                  <input
                    ref={contactFileInputRef}
                    type="file"
                    multiple
                    accept=".vcf,.vcard,text/vcard,text/x-vcard"
                    className="hidden"
                    onChange={handleVCardContactFile}
                    data-testid="contact-vcard-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowContactFileDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => contactFileInputRef.current?.click()}
                    disabled={importingPhoneContacts || loadingPhoneContacts}
                  >
                    {importingPhoneContacts ? <SpinnerGap className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    Choose .vcf Files
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={showContactSelectionDialog} onOpenChange={setShowContactSelectionDialog}>
              <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Select contacts to add as guests</DialogTitle>
                  <DialogDescription>
                    {phoneContacts.length} contact{phoneContacts.length === 1 ? '' : 's'} saved with this event. You can return at any time and add more of them as guests.
                  </DialogDescription>
                </DialogHeader>

                <div className="flex min-h-0 flex-1 flex-col gap-3">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1">
                      <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={phoneContactSearch}
                        onChange={(event) => setPhoneContactSearch(event.target.value)}
                        placeholder="Search contacts by name or phone"
                        className="pl-9"
                        data-testid="phone-contact-search"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleLoadPhoneContacts}
                      disabled={importingPhoneContacts || loadingPhoneContacts || addingSelectedPhoneContacts}
                    >
                      {importingPhoneContacts ? (
                        <SpinnerGap className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <AddressBook className="mr-2 h-4 w-4" />
                      )}
                      Load more
                    </Button>
                  </div>

                  <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="select-all-phone-contacts"
                        checked={allVisiblePhoneContactsSelected
                          ? true
                          : selectedVisiblePhoneContactCount > 0 ? 'indeterminate' : false}
                        onCheckedChange={(checked) => toggleVisiblePhoneContacts(checked === true)}
                        disabled={filteredPhoneContacts.length === 0}
                      />
                      <Label htmlFor="select-all-phone-contacts" className="cursor-pointer text-sm font-medium">
                        Select all available shown ({selectableFilteredPhoneContacts.length})
                      </Label>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {selectedPhoneContacts.length} selected
                    </span>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto rounded-md border" data-testid="phone-contact-list">
                    {filteredPhoneContacts.map((contact, index) => {
                      const contactKey = getPhoneContactKey(contact);
                      const checkboxId = `phone-contact-${index}`;
                      return (
                        <div
                          key={contactKey}
                          className={cn(
                            'flex items-center gap-3 border-b px-3 py-3 last:border-b-0',
                            selectedPhoneContactKeySet.has(contactKey) && 'bg-wedding-sage/10'
                          )}
                        >
                          <Checkbox
                            id={checkboxId}
                            checked={selectedPhoneContactKeySet.has(contactKey)}
                            onCheckedChange={(checked) => togglePhoneContact(contactKey, checked === true)}
                            disabled={contact.is_guest}
                          />
                          <Label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer">
                            <span className="flex items-center gap-2">
                              <span className="block min-w-0 truncate text-sm font-medium text-foreground">{contact.name}</span>
                              {contact.is_guest && (
                                <Badge variant="outline" className="shrink-0 text-[0.625rem] font-medium text-muted-foreground">
                                  Already a guest
                                </Badge>
                              )}
                            </span>
                            <span className="block truncate text-xs font-normal text-muted-foreground">{contact.phone}</span>
                          </Label>
                        </div>
                      );
                    })}
                    {filteredPhoneContacts.length === 0 && (
                      <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                        No contacts match your search.
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowContactSelectionDialog(false)}>
                    Close
                  </Button>
                  <Button
                    type="button"
                    onClick={handleAddSelectedPhoneContacts}
                    disabled={addingSelectedPhoneContacts || selectedPhoneContacts.length === 0}
                    data-testid="add-selected-phone-contacts"
                  >
                    {addingSelectedPhoneContacts && <SpinnerGap className="mr-2 h-4 w-4 animate-spin" />}
                    Add selected as guests ({selectedPhoneContacts.length})
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="hidden xl:inline-flex"
                onClick={() => setShowBulkDialog(true)}
                aria-label="Bulk upload guests"
                title="Bulk upload guests"
                data-header-action
                data-testid="bulk-upload-btn"
              >
                <Upload className="h-4 w-4" />
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Bulk Upload Guests</DialogTitle>
                  <DialogDescription>
                    Upload the generated Excel template. Each row represents one named guest; repeated phone numbers form one invitation group.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-4">
                  <div className="rounded-md border p-3 space-y-2">
                    <p className="text-sm font-medium">Step 1: Download template</p>
                    <p className="text-sm text-muted-foreground">
                      Add unnamed companions with the count column. Shared dietary
                      {eventTypeConfig.features.showAccommodation ? ', room' : ''}
                      {eventTypeConfig.features.showGuestSides ? ', side' : ''}
                      {eventTypeConfig.features.showItinerary ? ', and itinerary' : ''} values apply to the entire phone group.
                    </p>
                    {(eventTypeConfig.features.showAccommodation || eventTypeConfig.features.showItinerary) && (
                      <p className="text-sm text-muted-foreground">
                        {eventTypeConfig.features.showAccommodation ? 'Room settings default to YES. ' : ''}
                        {eventTypeConfig.features.showItinerary
                          ? 'Set All Itinerary Events to NO when selecting individual event columns.'
                          : ''}
                      </p>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDownloadGuestTemplate}
                      disabled={downloadingGuestTemplate}
                      data-testid="download-guest-template-btn"
                    >
                      <DownloadSimple className="w-4 h-4 mr-2" />
                      {downloadingGuestTemplate ? 'Downloading...' : 'Download Template'}
                    </Button>
                  </div>
                  <div className="rounded-md border p-3 space-y-2">
                    <p className="text-sm font-medium">Step 2: Upload completed template</p>
                    <Input 
                      type="file" 
                      accept=".xlsx,.xlsm"
                      onChange={handleBulkUpload}
                      data-testid="bulk-upload-input"
                    />
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog
              open={showAddDialog}
              onOpenChange={(open) => {
                setShowAddDialog(open);
                if (open) {
                  setNewGuest(createEmptyGuestGroupForm(allItineraryEventIds, eventTypeConfig.features.defaultGuestRequiresRoom));
                  setNewGuestItineraryCustomized(false);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button
                  className="bg-wedding-sage px-3 hover:bg-wedding-sage/90"
                  aria-label="Add Guest"
                  title="Add Guest"
                  data-header-action
                  data-testid="add-guest-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span>Add Guest</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Guests</DialogTitle>
                  <DialogDescription>Add one or more guest names under the same phone number</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddGuest} className="space-y-4">
                  {renderGuestGroupFields(
                    newGuest,
                    setNewGuest,
                    'guest',
                    () => setNewGuestItineraryCustomized(true)
                  )}
                  {renderGuestNameRows(newGuest, setNewGuest, 'guest')}
                  <DialogFooter>
                    <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90" data-testid="save-guest-btn">
                      Add Guest
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="px-3 xl:hidden"
                  aria-label="More guest actions"
                  title="More actions"
                  data-header-action
                  data-testid="mobile-guest-actions-btn"
                >
                  <DotsThree className="mr-2" style={{ width: 24, height: 24 }} weight="bold" />
                  <span>More</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 xl:hidden">
                <DropdownMenuItem
                  onClick={handleExportGuests}
                  disabled={exportingGuests}
                  data-testid="mobile-export-guests-btn"
                >
                  {exportingGuests ? (
                    <SpinnerGap className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <DownloadSimple className="mr-2 h-4 w-4" />
                  )}
                  {exportingGuests ? 'Exporting guest list...' : 'Export guest list'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleOpenPhoneContacts}
                  disabled={importingPhoneContacts || loadingPhoneContacts}
                  data-testid="mobile-import-contacts-btn"
                >
                  <AddressBook className="mr-2 h-4 w-4" />
                  {phoneContacts.length > 0
                    ? `Select loaded contacts (${phoneContacts.length})`
                    : isPhoneContactPickerSupported() ? 'Import phone contacts' : 'Import contacts from vCard'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowBulkDialog(true)}
                  data-testid="mobile-bulk-upload-btn"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Bulk upload guests
                </DropdownMenuItem>
                {isEventManager && (
                  <DropdownMenuItem
                    onClick={openStatusFieldsDialog}
                    data-testid="mobile-status-fields-btn"
                  >
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Customize status fields
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </StickyActionHeader>

        <Dialog open={showStatusFieldsDialog} onOpenChange={setShowStatusFieldsDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Guest status fields</DialogTitle>
            </DialogHeader>
            <div className="divide-y border-y">
              {visibleGuestStatusOptions.map((option) => {
                const StatusIcon = option.icon;
                const checked = draftStatusFields.includes(option.id);
                return (
                  <div key={option.id} className="flex items-center justify-between gap-4 py-3">
                    <Label
                      htmlFor={`guest-status-field-${option.id}`}
                      className="flex cursor-pointer items-center gap-3 text-sm font-medium"
                    >
                      <span className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded-full border',
                        checked
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 bg-slate-50 text-slate-400'
                      )}>
                        <StatusIcon className="h-4 w-4" />
                      </span>
                      {option.label}
                    </Label>
                    <Switch
                      id={`guest-status-field-${option.id}`}
                      checked={checked}
                      onCheckedChange={(enabled) => toggleStatusField(option.id, enabled)}
                    />
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowStatusFieldsDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-wedding-sage hover:bg-wedding-sage/90"
                onClick={saveStatusFields}
                disabled={savingStatusFields}
              >
                {savingStatusFields ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Search */}
        <Card
          className="!mt-2 sticky top-[calc(9rem+env(safe-area-inset-top))] z-20 overflow-hidden border-[#E9E2D8] bg-[#F8F6F2]/95 shadow-[0_14px_34px_rgba(47,42,38,0.1)] backdrop-blur-md md:top-14"
        >
          <CardContent className="p-3 sm:p-4">
            <div className="space-y-3.5">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="relative min-w-0 flex-1">
                  <MagnifyingGlass className="absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#8A8178]" />
                  <Input
                    placeholder="Search guests by name, email or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 rounded-xl border-[#E9E2D8] bg-white/95 pl-10 pr-4 text-[#2F2A26] shadow-[0_8px_18px_rgba(47,42,38,0.05)] placeholder:text-[#8A8178] focus-visible:ring-[#C9A86A]/35"
                    data-testid="guest-search-input"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="relative h-10 w-10 shrink-0 rounded-xl border-[#E9E2D8] bg-white/95 p-0 sm:w-auto sm:px-4"
                  onClick={() => handleFiltersOpenChange(true)}
                  aria-label="Open guest filters"
                  title="Filters"
                  data-testid="guest-filters-btn"
                >
                  <FunnelSimple className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Filters</span>
                  {getActiveFilterCount() > 0 && (
                    <Badge className="absolute -right-1.5 -top-1.5 h-5 min-w-5 rounded-full bg-wedding-sage px-1 text-[0.625rem] text-white sm:static sm:ml-2 sm:h-auto sm:min-w-0 sm:px-2 sm:text-xs">
                      {getActiveFilterCount()}
                    </Badge>
                  )}
                </Button>
                {hasAdminAccess && selectedBulkGuestIds.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 shrink-0 rounded-xl border-[#D8C3E8] bg-[#F8F1FC] px-3 text-[#74438E] hover:border-[#BE9CD3] hover:bg-[#F1E5F8] hover:text-[#5E3475]"
                    onClick={handleGroupSelectedGuests}
                    disabled={groupingGuests}
                    data-testid="group-selected-guests-btn"
                  >
                    {groupingGuests
                      ? <SpinnerGap className="h-4 w-4 animate-spin sm:mr-2" />
                      : <UsersThree className="h-4 w-4 sm:mr-2" />}
                    <span className="hidden sm:inline">Group guests</span>
                    <span className="ml-1">({selectedBulkGuestIds.length})</span>
                  </Button>
                )}
                {hasAdminAccess && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          'relative h-10 w-10 shrink-0 rounded-xl border-[#E9E2D8] bg-white/95 p-0'
                        )}
                        disabled={deletingAllGuests}
                        aria-label="Guest bulk actions"
                        title="Actions"
                        data-testid="bulk-actions-btn"
                      >
                        <DotsThree
                          style={{ width: 24, height: 24 }}
                          weight="bold"
                        />
                        {selectedBulkGuestIds.length > 0 && (
                          <span
                            className="absolute -right-1 -top-1 flex h-3 w-3 items-center rounded-full bg-wedding-sage pl-[0.4rem]"
                            aria-hidden="true"
                            data-testid="bulk-actions-selection-indicator"
                          >
                            <span className="h-1 w-1 -translate-x-1/2 rounded-full bg-white" />
                          </span>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="text-destructive"
                        disabled={selectedBulkGuestIds.length === 0}
                        onClick={() => {
                          setBulkDeleteMode('selected');
                          setBulkDeleteConfirmation('');
                          setShowBulkDeleteDialog(true);
                        }}
                        data-testid="bulk-action-delete-guests"
                      >
                        <Trash className="mr-2 h-4 w-4" />
                        Delete selected guests
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setBulkDeleteMode('all');
                          setBulkDeleteConfirmation('');
                          setShowBulkDeleteDialog(true);
                        }}
                        data-testid="bulk-action-delete-all-guests"
                      >
                        <Trash className="mr-2 h-4 w-4" />
                        Delete all guests
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {activeFilterChips.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#E9E2D8] bg-white/80 px-3 py-2" data-testid="guest-active-filters">
                  <span className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8A8178]">Active filters</span>
                  {activeFilterChips.map((chip) => (
                    <Badge
                      key={chip.key}
                      variant="outline"
                      className="flex min-h-7 max-w-full items-center gap-1.5 rounded-full border-[#D8CDBD] bg-[#F8F6F2] px-2.5 py-1 text-[#2F2A26]"
                    >
                      <span className="truncate">{chip.label}</span>
                      <button
                        type="button"
                        onClick={() => removeFilterChip(chip.type, chip.value)}
                        className="ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[#8A8178] transition-colors hover:bg-white hover:text-[#2F2A26] focus:outline-none focus:ring-2 focus:ring-[#C9A86A]/40"
                        aria-label={`Remove ${chip.label} filter`}
                        data-testid={`remove-filter-${chip.key}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </Badge>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="h-7 rounded-full px-2 text-xs text-[#6F655C]"
                    data-testid="guest-active-filters-clear"
                  >
                    Clear all
                  </Button>
                </div>
              )}

            </div>
          </CardContent>
        </Card>

        <Sheet open={showFiltersSheet} onOpenChange={handleFiltersOpenChange}>
          <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-md" data-testid="guest-filters-sheet">
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
              <SheetDescription>Refine the guest list for this event.</SheetDescription>
            </SheetHeader>
            <div className="flex-1 space-y-5 py-4">
              {eventTypeConfig.features.showGuestSides && <div className="rounded-xl border border-[#E9E2D8] bg-white/90 p-1.5 shadow-[0_8px_20px_rgba(47,42,38,0.06)]">
                <div className="mb-1 px-2 text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-[#8A8178]">
                  Guest Side
                </div>
                <div className="relative grid grid-cols-3 rounded-xl bg-[#F8F6F2] p-1">
                  <div
                    className={cn(
                      'absolute inset-y-1 w-[calc(33.333%_-_0.25rem)] rounded-lg bg-white shadow-[0_5px_5px_rgba(47,42,38,0.1)] transition-transform duration-200',
                      draftFilters.side === 'bride' && 'translate-x-0',
                      draftFilters.side === 'all' && 'translate-x-full',
                      draftFilters.side === 'groom' && 'translate-x-[200%]'
                    )}
                  />
                  {SIDE_SEGMENTS.map((segment) => {
                    const SegmentIcon = segment.icon;
                    return (
                      <button
                        key={segment.value}
                        type="button"
                        onClick={() => setDraftFilters((prev) => ({ ...prev, side: segment.value }))}
                        className={cn(
                          'relative z-10 flex min-h-9 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[0.8125rem] font-semibold transition-colors',
                          draftFilters.side === segment.value ? 'text-[#2F2A26]' : 'text-[#8A8178] hover:text-[#2F2A26]'
                        )}
                      >
                        <SegmentIcon
                          className={cn(
                            'h-3.5 w-3.5',
                            segment.value === 'bride' && 'text-[#B9827B]',
                            segment.value === 'groom' && 'text-[#6E8267]',
                            segment.value === 'all' && 'text-[#C9A86A]'
                          )}
                          weight={draftFilters.side === segment.value ? 'fill' : 'regular'}
                        />
                        {segment.label}
                      </button>
                    );
                  })}
                </div>
              </div>}

              <PremiumFilterSelect
                value={draftFilters.rsvp}
                onValueChange={(value) => setDraftFilters((prev) => ({ ...prev, rsvp: value }))}
                label="RSVP"
                icon={Heart}
                options={RSVP_FILTER_OPTIONS}
                tone="rose"
              />
              {eventTypeConfig.features.showTravelAndId && <>
              <PremiumFilterSelect
                value={draftFilters.idUpload}
                onValueChange={(value) => setDraftFilters((prev) => ({ ...prev, idUpload: value }))}
                label="ID Uploads"
                icon={IdentificationCard}
                options={UPLOAD_FILTER_OPTIONS}
                tone="gold"
              />
              <PremiumFilterSelect
                value={draftFilters.arrivalUpload}
                onValueChange={(value) => setDraftFilters((prev) => ({ ...prev, arrivalUpload: value }))}
                label="Arrival Details"
                icon={AirplaneTakeoff}
                options={UPLOAD_FILTER_OPTIONS}
                tone="sage"
              />
              <PremiumFilterSelect
                value={draftFilters.departureUpload}
                onValueChange={(value) => setDraftFilters((prev) => ({ ...prev, departureUpload: value }))}
                label="Departure Details"
                icon={AirplaneLanding}
                options={UPLOAD_FILTER_OPTIONS}
                tone="plum"
              />
              </>}

              {eventTypeConfig.features.showItinerary && <div className="rounded-xl border border-[#E9E2D8] bg-white/90 p-3 shadow-[0_8px_20px_rgba(47,42,38,0.06)]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label>Itinerary Events</Label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Guests must match all selected itinerary events.
                    </p>
                  </div>
                  <Badge variant="outline" className="shrink-0 border-[#D8CDBD] bg-[#F8F6F2] text-[#6F655C]">
                    {(draftFilters.itineraryEventIds || []).length} selected
                  </Badge>
                </div>
                {sortedItineraryItems.length > 0 ? (
                  <>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setDraftFilters((prev) => ({
                            ...prev,
                            itineraryEventIds: sortedItineraryItems.map((item) => item.id).filter(Boolean)
                          }))
                        }
                        className="h-8 rounded-full border-[#D8CDBD] px-3 text-xs"
                        data-testid="filter-itinerary-select-all"
                      >
                        Select all
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDraftFilters((prev) => ({ ...prev, itineraryEventIds: [] }))}
                        className="h-8 rounded-full px-3 text-xs text-[#6F655C]"
                        data-testid="filter-itinerary-clear"
                      >
                        Clear all
                      </Button>
                    </div>
                    <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
                      {sortedItineraryItems.map((item) => {
                        const checked = (draftFilters.itineraryEventIds || []).includes(item.id);
                        return (
                          <label
                            key={item.id}
                            className={cn(
                              'flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors',
                              checked
                                ? 'border-[#C9A86A] bg-[#FFF9ED] text-[#2F2A26]'
                                : 'border-[#E9E2D8] bg-white text-[#2F2A26] hover:bg-[#F8F6F2]'
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(checked) => toggleFilterItinerary(item.id, checked === true)}
                              data-testid={`filter-itinerary-${item.id}`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">{item.name || 'Untitled event'}</span>
                              <span className="mt-0.5 block text-xs text-[#8A8178]">{getItineraryDateLabel(item)}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="mt-3 rounded-xl border border-[#E9E2D8] bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                    No itinerary events available yet.
                  </p>
                )}
              </div>}
            </div>
            <SheetFooter className="gap-2">
              <Button type="button" variant="outline" onClick={resetFilters} data-testid="guest-filters-reset-btn">
                Reset
              </Button>
              <Button type="button" className="bg-wedding-sage hover:bg-wedding-sage/90" onClick={applyFilters} data-testid="guest-filters-apply-btn">
                Apply
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <AlertDialog
          open={showBulkDeleteDialog}
          onOpenChange={(open) => {
            setShowBulkDeleteDialog(open);
            if (!open && !deletingAllGuests) {
              setBulkDeleteConfirmation('');
              setBulkDeleteMode('selected');
            }
          }}
        >
          <AlertDialogContent className={cn('gap-0 overflow-hidden p-0', bulkDeleteMode === 'all' ? 'sm:max-w-[480px]' : 'sm:max-w-[420px]')}>
            <AlertDialogHeader>
              <div className="px-6 pb-4 pt-6">
                <AlertDialogTitle className="text-xl font-semibold tracking-normal text-[#17231F]">
                  {bulkDeleteMode === 'all' ? 'Delete all guests?' : 'Delete selected guests?'}
                </AlertDialogTitle>
                <AlertDialogDescription className="mt-2 text-sm leading-6 text-[#6F746F]">
                  {bulkDeleteMode === 'all'
                    ? 'Every guest in this event will be removed, including their room assignments.'
                    : `Delete ${selectedBulkGuestIds.length} selected guest${selectedBulkGuestIds.length === 1 ? '' : 's'} from this event?`}
                </AlertDialogDescription>
              </div>
            </AlertDialogHeader>
            {bulkDeleteMode === 'all' && (
              <div className="mx-6 mb-6 rounded-lg border border-[#DED8CC] bg-[#F7F5F1] p-4">
                <div className="mb-4 flex gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-[#716454] shadow-sm ring-1 ring-[#E4DED4]">
                    <Trash className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#292E2B]">Confirmation required</p>
                    <p className="mt-1 text-sm leading-5 text-[#74716B]">
                      Type <span className="font-semibold text-[#474A46]">{DELETE_ALL_CONFIRMATION}</span> below to continue.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulk-delete-confirmation" className="text-xs font-medium text-[#5F645F]">
                    Confirmation phrase
                  </Label>
                  <Input
                    id="bulk-delete-confirmation"
                    value={bulkDeleteConfirmation}
                    onChange={(e) => setBulkDeleteConfirmation(e.target.value)}
                    placeholder={DELETE_ALL_CONFIRMATION}
                    autoComplete="off"
                    className="h-10 border-[#D8D2C8] bg-white focus-visible:ring-[#6C7D72]"
                    data-testid="bulk-delete-confirmation-input"
                  />
                </div>
              </div>
            )}
            <AlertDialogFooter className="border-t border-[#E7E4DE] bg-[#FBFAF8] px-6 py-4">
              <AlertDialogCancel disabled={deletingAllGuests}>Cancel</AlertDialogCancel>
              <Button
                type="button"
                variant="destructive"
                disabled={
                  (bulkDeleteMode === 'all' && bulkDeleteConfirmation !== DELETE_ALL_CONFIRMATION) ||
                  deletingAllGuests ||
                  (bulkDeleteMode !== 'all' && selectedBulkGuestIds.length === 0)
                }
                onClick={handleBulkDeleteGuests}
                data-testid="confirm-bulk-delete-guests-btn"
              >
                {deletingAllGuests ? (
                  <>
                    <SpinnerGap className="h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash className="h-4 w-4" />
                    {bulkDeleteMode === 'all'
                      ? 'Delete all guests'
                      : `Delete ${selectedBulkGuestIds.length} guest${selectedBulkGuestIds.length === 1 ? '' : 's'}`}
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Guest Table */}
        <Card className="border-0 bg-transparent shadow-none sm:border sm:bg-card sm:shadow-sm">
          <CardContent className="p-0">
            <TooltipProvider delayDuration={120}>
              <Table className="block w-full sm:table">
                <TableHeader className="hidden sm:table-header-group">
                  <TableRow>
                    {hasAdminAccess && (
                      <TableHead className="w-12">
                        <Checkbox
                          checked={someVisibleGuestsSelected ? 'indeterminate' : allVisibleGuestsSelected}
                          onCheckedChange={(checked) => handleToggleAllVisibleGuests(checked === true)}
                          disabled={visibleGuestIds.length === 0}
                          aria-label="Select all visible guests"
                          data-testid="select-all-guests-checkbox"
                        />
                      </TableHead>
                    )}
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Contact</TableHead>
                    {(hasEnabledStatusFields || isEventManager) && (
                      <TableHead className="min-w-[250px]">
                        <div className="flex items-center gap-1.5">
                          <span>Status</span>
                          {isEventManager && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  onClick={openStatusFieldsDialog}
                                  aria-label="Customize guest status fields"
                                  data-testid="status-fields-btn"
                                >
                                  <SlidersHorizontal className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Customize guest status fields</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableHead>
                    )}
                    <TableHead>Invite Status</TableHead>
                    <TableHead className={hasAdminAccess ? 'w-20' : 'w-10'}></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="block space-y-2 p-0 sm:table-row-group sm:space-y-0 sm:p-0">
                  {displayGuests.map(({ guest, indent, isPrimary, accompanyingCount, groupSize, visualDisplayGroupId, displayGroupPosition }) => (
                    <TableRow 
                      key={guest.id} 
                      className={cn(
                        'relative grid grid-cols-[minmax(0,1fr)_auto] gap-2 rounded-lg border bg-background p-2.5 shadow-sm hover:bg-wedding-sage/5 sm:table-row sm:rounded-none sm:border-x-0 sm:border-t-0 sm:p-0 sm:shadow-none',
                        indent && 'bg-wedding-sage/5'
                      )}
                      onClick={() => viewGuestProfile(guest)}
                      data-testid={`guest-row-${guest.id}`}
                    >
                      {hasAdminAccess && (
                        <TableCell
                          className="absolute left-3 top-5 z-20 p-0 sm:relative sm:left-auto sm:top-auto sm:bg-card sm:p-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selectedBulkGuestIds.includes(guest.id)}
                            onCheckedChange={(checked) => handleToggleBulkGuest(guest.id, checked === true)}
                            aria-label={`Select ${guest.name}`}
                            data-testid={`select-guest-${guest.id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell
                        className={cn(
                          'col-start-1 row-start-1 min-w-0 p-0 sm:table-cell sm:p-2',
                          hasAdminAccess && 'pl-9 sm:pl-4'
                        )}
                      >
                        <div className={`flex min-w-0 items-center gap-3 ${indent ? 'pl-8 border-l-2 border-wedding-sage/30' : ''}`}>
                          <div
                            className={cn(
                              `${indent ? 'h-7 w-7' : 'h-9 w-9'} relative flex shrink-0 items-center justify-center rounded-full`,
                              displayGroupPosition ? 'bg-[#E9D8F2]' : 'bg-wedding-sage/10'
                            )}
                            data-display-group-id={visualDisplayGroupId || guest.display_group_id || undefined}
                          >
                            {displayGroupPosition && (
                              <span
                                aria-hidden="true"
                                className={cn(
                                  'pointer-events-none absolute -inset-x-1 z-0 bg-[#E9D8F2]/80',
                                  displayGroupPosition === 'first' && '-bottom-4 top-0 rounded-t-full',
                                  displayGroupPosition === 'middle' && '-bottom-4 -top-4',
                                  displayGroupPosition === 'last' && 'bottom-0 -top-4 rounded-b-full'
                                )}
                              />
                            )}
                            <span className={cn('relative z-10 text-sm font-medium', displayGroupPosition ? 'text-[#74438E]' : 'text-wedding-sage')}>
                              {guest.name.charAt(0).toUpperCase()}
                            </span>
                            {accompanyingCount > 0 && (
                              <span
                                className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[0.625rem] leading-none font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                                data-testid={`accompanying-badge-${guest.id}`}
                                title={`Bringing ${accompanyingCount} accompanying guest(s)`}
                              >
                                +{accompanyingCount}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-1.5">
                              <p className="min-w-0 break-words font-medium">{guest.name}</p>
                              {hasAdminAccess && eventTypeConfig.features.showGuestGiftTracking && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className={cn(
                                        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors',
                                        guest.has_received_gift
                                          ? 'border-emerald-300 bg-emerald-100 text-emerald-700 shadow-sm ring-1 ring-emerald-200/70'
                                          : 'border-slate-300 bg-slate-50 text-slate-500 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700'
                                      )}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openReceivedGiftDialog(guest);
                                      }}
                                      aria-label={`${guest.has_received_gift ? 'View' : 'Record'} gift for ${guest.name}`}
                                      data-gift-recorded={guest.has_received_gift ? 'true' : 'false'}
                                      data-testid={`guest-gift-${guest.id}`}
                                    >
                                      <Gift className="h-3.5 w-3.5" weight={guest.has_received_gift ? 'fill' : 'regular'} />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {guest.has_received_gift ? 'Gift recorded — view or update' : 'Record gift or amount'}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {guest.phone ? (
                                <a
                                  href={`tel:${guest.phone}`}
                                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-slate-50 text-slate-600 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 sm:hidden"
                                  aria-label={`Call ${guest.name}`}
                                  title={`Call ${guest.phone}`}
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <Phone className="h-3.5 w-3.5" />
                                </a>
                              ) : (
                                <span
                                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-300 sm:hidden"
                                  aria-label={`No phone number for ${guest.name}`}
                                  title="No phone number"
                                >
                                  <Phone className="h-3.5 w-3.5" />
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              {guest.is_admin && (
                                <Badge variant="outline" className="text-xs">Admin</Badge>
                              )}
                              {indent && (
                                <Badge variant="outline" className="text-[0.625rem] text-muted-foreground border-muted">
                                  Same phone
                                </Badge>
                              )}
                              {!indent && groupSize > 1 && (
                                <Badge variant="outline" className="text-[0.625rem] text-muted-foreground border-muted">
                                  Shared phone
                                </Badge>
                              )}
                              {/* {getGuestSideBadge(guest.guest_side)} */}
                            </div>
                            {guest.email && (
                              <div className="mt-2 space-y-1 text-xs text-muted-foreground sm:hidden">
                                <a
                                  href={`mailto:${guest.email}`}
                                  className="flex min-w-0 items-center gap-1.5 hover:text-wedding-sage"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <Envelope className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate">{guest.email}</span>
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-1">
                          {guest.email && (
                            <a
                              href={`mailto:${guest.email}`}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-wedding-sage"
                              aria-label={`Email ${guest.name}`}
                              title={guest.email}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Envelope className="h-4 w-4" />
                            </a>
                          )}
                          {guest.phone ? (
                            <a
                              href={`tel:${guest.phone}`}
                              className="inline-flex min-h-8 max-w-[190px] items-center rounded-md px-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-wedding-sage"
                              aria-label={`Call ${guest.name}`}
                              title={guest.phone}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <span className="truncate">{guest.phone}</span>
                            </a>
                          ) : (
                            <span className="inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 text-sm text-orange-600">
                              <Warning className="h-4 w-4 shrink-0" aria-hidden="true" />
                              No phone
                            </span>
                          )}
                        </div>
                      </TableCell>
                      {hasEnabledStatusFields && (
                        <TableCell className="col-span-2 row-start-2 border-t p-0 pt-2 sm:table-cell sm:border-0 sm:p-2">
                          <span className="mb-2 block text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground sm:hidden">
                            Guest status
                          </span>
                          <div className="flex flex-wrap items-center gap-1">
                          {enabledStatusFields.has('rsvp') && renderStatusIcon({
                            icon: CheckCircle,
                            label: `RSVP: ${guest.rsvp_status || 'pending'}`,
                            state: guest.rsvp_status === 'declined'
                              ? 'declined'
                              : guest.rsvp_status === 'confirmed'
                                ? 'complete'
                                : 'pending'
                          })}
                          {enabledStatusFields.has('id_upload') && renderStatusIcon({
                            icon: IdentificationCard,
                            label: hasIdUpload(guest) ? 'ID uploaded' : 'ID upload pending',
                            state: hasIdUpload(guest) ? 'complete' : 'pending'
                          })}
                          {enabledStatusFields.has('arrival') && renderStatusIcon({
                            icon: AirplaneTakeoff,
                            label: hasArrivalUpload(guest) ? 'Arrival uploaded' : 'Arrival upload pending',
                            state: hasArrivalUpload(guest) ? 'complete' : 'pending'
                          })}
                          {enabledStatusFields.has('departure') && renderStatusIcon({
                            icon: AirplaneLanding,
                            label: hasDepartureUpload(guest) ? 'Departure uploaded' : 'Departure upload pending',
                            state: hasDepartureUpload(guest) ? 'complete' : 'pending'
                          })}
                          {enabledStatusFields.has('room') && guestRequiresRoom(guest) && renderStatusIcon({
                            icon: Bed,
                            label: getGuestRoomStatusLabel(guest),
                            state: guest.rsvp_status === 'declined'
                              ? 'not_required'
                              : isGuestRoomAllocated(guest)
                                ? 'complete'
                                : 'pending',
                            badgeText: (
                              isPrimary &&
                              getGuestRoomAllocationState(guest) === 'partial' &&
                              getGuestRoomPendingCount(guest) > 0
                            )
                              ? `${getGuestRoomPendingCount(guest)} pending`
                              : null
                          })}
                          {enabledStatusFields.has('guest_side') && renderGuestSideStatus(guest)}
                          <div className="ml-auto flex items-center gap-1 sm:hidden">
                            {renderWhatsAppInviteAction(guest)}
                            {getWhatsAppStatusBadge(guest)}
                          </div>
                          </div>
                        </TableCell>
                      )}
                      {!hasEnabledStatusFields && isEventManager && (
                        <TableCell className="hidden sm:table-cell" />
                      )}
                      <TableCell
                        className={cn(
                          hasEnabledStatusFields
                            ? 'hidden sm:table-cell sm:p-2'
                            : 'col-span-2 row-start-2 flex items-center justify-between border-t p-0 pt-3 sm:table-cell sm:border-0 sm:p-2'
                        )}
                      >
                        {!hasEnabledStatusFields && (
                          <span className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground sm:hidden">
                            Invite status
                          </span>
                        )}
                        <div className="flex items-center gap-1">
                          {renderWhatsAppInviteAction(guest)}
                          {getWhatsAppStatusBadge(guest)}
                        </div>
                      </TableCell>
                      <TableCell className="col-start-2 row-start-1 p-0 sm:table-cell sm:p-2">
                        <div className="flex items-center justify-end gap-0.5" onClick={(event) => event.stopPropagation()}>
                          {hasAdminAccess && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditGuest(guest)}
                                  aria-label={`Edit ${guest.name}`}
                                  data-testid={`edit-guest-${guest.id}`}
                                >
                                  <PencilSimple className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit guest</TooltipContent>
                            </Tooltip>
                          )}
                          <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <DotsThree style={{ width: 24, height: 24 }} weight="bold" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); viewGuestProfile(guest); }}>
                              <UserCircle className="w-4 h-4 mr-2" /> View Profile
                            </DropdownMenuItem>
                            {hasAdminAccess && (
                              <>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleToggleAdmin(guest); }}>
                                  <Shield className="w-4 h-4 mr-2" /> 
                                  {guest.is_admin ? 'Remove Admin' : 'Make Admin'}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => { e.stopPropagation(); handleDeleteGuest(guest); }}
                                  className="text-destructive"
                                >
                                  <Trash className="w-4 h-4 mr-2" /> Delete Guest
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredGuests.length === 0 && !loading && (
                    <TableRow>
                      <TableCell
                        colSpan={(hasAdminAccess ? 6 : 5) - ((hasEnabledStatusFields || isEventManager) ? 0 : 1)}
                        className="col-span-2 text-center py-8 text-muted-foreground sm:table-cell"
                      >
                        {guests.length === 0 ? 'No guests found. Add your first guest to get started.' : 'No guests match the current filters.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TooltipProvider>
            <div ref={loadMoreGuestsRef} className="min-h-6 border-t px-4 py-3 text-center text-sm text-muted-foreground">
              {loadingMoreGuests
                ? 'Loading more guests...'
                : hasMoreGuests
                  ? 'Scroll to load more guests'
                  : guests.length > GUEST_PAGE_SIZE
                    ? 'All matching guests loaded'
                    : null}
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Send Dialog */}
        <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <WhatsappLogo className="w-6 h-6 text-green-600" weight="fill" />
                Send WhatsApp Notifications
              </DialogTitle>
              <DialogDescription>
                Send personalized {eventTypeConfig.labels.invitationSubject} invitations and notifications via WhatsApp to your guests
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {whatsappMode === 'invite' && themeMissing && (
                <div className="relative overflow-hidden rounded-md border border-[#E5D9C3] bg-[#FCF9F2] px-4 py-3" role="note">
                  <span className="absolute inset-y-0 left-0 w-0.5 bg-[#B69B6B]" aria-hidden="true" />
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E2D5BD] bg-white text-[#806F52]">
                        <Palette className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-[#403B33]">Make every guest page feel like your event</p>
                          <span className="flex items-center gap-1 text-[0.6875rem] font-medium text-[#8A744C]">
                            <Sparkle className="h-3 w-3" weight="fill" />
                            Recommended
                          </span>
                        </div>
                        <p className="mt-1 text-xs leading-5 text-[#777167]">
                          Invitations are ready to send. Add your colors and typography for a more personal guest experience.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0 self-start border-[#D9C9AA] bg-white px-3 text-xs text-[#665B49] hover:bg-[#F7F2E8] sm:self-center"
                      onClick={() => { setShowWhatsAppDialog(false); navigate('/dashboard/theme'); }}
                    >
                      Theme &amp; Branding
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}

              {whatsappMode === 'invite' && (
                <div className="rounded-md border bg-muted/25 px-4 py-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Sparkle className="h-4 w-4 text-primary" weight="fill" />
                        {eventTypeConfig.features.showGuestSides ? 'Side-wise invite media' : 'Invite media'}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {eventTypeConfig.features.showGuestSides
                          ? 'Prepare bride side, groom side, both side, and event-wise invite images or videos in Invite Studio.'
                          : 'Prepare full-event and event-wise invite images or videos in Invite Studio.'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0 self-start px-3 text-xs sm:self-center"
                      onClick={() => { setShowWhatsAppDialog(false); navigate('/dashboard/theme?tab=invites'); }}
                    >
                      Open Invite Studio
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}

              {whatsappMode === 'invite' && (
                <div className="rounded-md border bg-background px-4 py-3">
                  <div className="grid gap-3 sm:grid-cols-[1fr_13rem] sm:items-end">
                    <div className="space-y-1">
                      <Label htmlFor="whatsapp-invite-pack">Invite media</Label>
                      <Select value={selectedInvitePackId} onValueChange={setSelectedInvitePackId}>
                        <SelectTrigger id="whatsapp-invite-pack">
                          <SelectValue placeholder={loadingInvitePacks ? 'Loading invite media...' : 'Choose invite media'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__auto__">
                            {eventTypeConfig.features.showGuestSides ? 'Auto side-wise invite' : 'Auto matching invite'}
                          </SelectItem>
                          {inviteMediaOptions.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {invitePackLabel(item)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs leading-5 text-muted-foreground">
                        {selectedInvitePack
                          ? 'This exact saved invite image or video will be sent to every selected recipient.'
                          : eventTypeConfig.features.showGuestSides
                            ? 'Auto uses the matching bride, groom, both, or default wedding invite for each guest group.'
                            : `Auto uses the matching default or event-wise ${eventTypeConfig.labels.invitationSubject} invite for each guest group.`}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 justify-center"
                      onClick={() => { setShowWhatsAppDialog(false); navigate('/dashboard/theme?tab=invites'); }}
                    >
                      Open Studio
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}

              {guestsWithoutPhone.length > 0 && (
                <Alert>
                  <Phone className="w-4 h-4" />
                  <AlertTitle>{guestsWithoutPhone.length} guests without phone numbers</AlertTitle>
                  <AlertDescription>
                    The following guests don&apos;t have phone numbers: {guestsWithoutPhone.slice(0, 5).join(', ')}
                    {guestsWithoutPhone.length > 5 && ` and ${guestsWithoutPhone.length - 5} more`}
                  </AlertDescription>
                </Alert>
              )}

              {/* Guest Selection */}
              <div className="border rounded-lg">
                <Tabs value={whatsappMode} onValueChange={(value) => {
                  setWhatsappMode(value);
                  setSelectedGuestIds([]);
                }}>
                  <div className="p-3 bg-muted/50 border-b space-y-3">
                    <TabsList className={`grid w-full ${eventTypeConfig.features.showTravelAndId ? 'grid-cols-4' : 'grid-cols-2'}`}>
                      <TabsTrigger value="invite">Invite</TabsTrigger>
                      <TabsTrigger value="rsvp">RSVP</TabsTrigger>
                      {eventTypeConfig.features.showTravelAndId && <TabsTrigger value="id_upload">ID Upload</TabsTrigger>}
                      {eventTypeConfig.features.showTravelAndId && <TabsTrigger value="travel_upload">Travel Details</TabsTrigger>}
                    </TabsList>
                    <p className="text-sm text-muted-foreground">{currentWhatsAppMode.description}</p>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`select-all-${whatsappMode}`}
                          checked={visibleSelectedGuestIds.length === visibleWhatsAppGuests.filter(isWhatsAppEligible).length && visibleSelectedGuestIds.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              const eligibleVisibleGuests = visibleWhatsAppGuests.filter(isWhatsAppEligible);
                              setSelectedGuestIds(eligibleVisibleGuests.map((guest) => guest.id));
                            } else {
                              setSelectedGuestIds([]);
                            }
                          }}
                        />
                        <Label htmlFor={`select-all-${whatsappMode}`} className="text-sm font-medium">
                          Select All ({visibleWhatsAppGuests.filter(isWhatsAppEligible).length} eligible)
                        </Label>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {visibleSelectedGuestIds.length} selected
                      </span>
                    </div>
                  </div>

                  <TabsContent value={whatsappMode} className="mt-0">
                    <div className="max-h-60 overflow-y-auto">
                      {visibleWhatsAppGuests.map((guest) => {
                        const isEligible = isWhatsAppEligible(guest);

                        return (
                          <div 
                            key={`${whatsappMode}-${guest.id}`}
                            className={`flex items-center justify-between p-3 border-b last:border-b-0 ${!isEligible ? 'bg-muted/30' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                id={`guest-${whatsappMode}-${guest.id}`}
                                checked={selectedGuestIds.includes(guest.id)}
                                onCheckedChange={(checked) => handleSelectGuest(guest.id, checked)}
                                disabled={!isEligible}
                              />
                              <div>
                                <Label htmlFor={`guest-${whatsappMode}-${guest.id}`} className="font-medium">
                                  {guest.name}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  {guest.phone || 'No phone number'}
                                </p>
                              </div>
                            </div>
                            <div>
                              {getWhatsAppStatusBadge(guest)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowWhatsAppDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const recipientCount = whatsappMode === 'invite'
                    ? whatsappReadiness?.guests_with_valid_phone || 0
                    : visibleWhatsAppGuests.filter(isWhatsAppEligible).length;
                  setPendingWhatsAppSend({
                    kind: whatsappMode === 'invite' ? 'invite' : 'reminder',
                    reminderType: whatsappMode,
                    sendToAll: true,
                    recipientCount,
                    label: currentWhatsAppMode.label,
                    invitePackLabel: whatsappMode === 'invite' && selectedInvitePack ? invitePackLabel(selectedInvitePack) : null,
                  });
                }}
                disabled={
                  sendingWhatsApp ||
                  (whatsappMode === 'invite'
                    ? whatsappReadiness?.guests_with_valid_phone === 0
                    : visibleWhatsAppGuests.length === 0)
                }
                variant="outline"
                className="text-green-600 border-green-200 hover:bg-green-50"
              >
                {sendingWhatsApp ? (
                  <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <WhatsappLogo className="w-4 h-4 mr-2" weight="fill" />
                )}
                {whatsappMode === 'invite'
                  ? `Send to All (${whatsappReadiness?.guests_with_valid_phone || 0})`
                  : `Send to All (${visibleWhatsAppGuests.filter(isWhatsAppEligible).length})`}
              </Button>
              <Button
                onClick={() => {
                  setPendingWhatsAppSend({
                    kind: whatsappMode === 'invite' ? 'invite' : 'reminder',
                    reminderType: whatsappMode,
                    sendToAll: false,
                    recipientCount: visibleSelectedGuestIds.length,
                    label: currentWhatsAppMode.label,
                    invitePackLabel: whatsappMode === 'invite' && selectedInvitePack ? invitePackLabel(selectedInvitePack) : null,
                  });
                }}
                disabled={sendingWhatsApp || visibleSelectedGuestIds.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {sendingWhatsApp ? (
                  <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <PaperPlaneTilt className="w-4 h-4 mr-2" />
                )}
                {whatsappMode === 'invite'
                  ? `Send to Selected (${visibleSelectedGuestIds.length})`
                  : `Send to Selected (${visibleSelectedGuestIds.length})`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <WhatsAppSendConfirmationDialog
          open={Boolean(pendingWhatsAppSend)}
          onOpenChange={(open) => !open && setPendingWhatsAppSend(null)}
          onConfirm={confirmWhatsAppSend}
          title={pendingWhatsAppSend?.kind === 'single_invite'
            ? `Send invitation to ${pendingWhatsAppSend.guestName || 'this guest'}?`
            : pendingWhatsAppSend?.kind === 'invite'
              ? 'Send WhatsApp invitations?'
              : `Send ${pendingWhatsAppSend?.label || 'WhatsApp reminder'}?`}
          description={pendingWhatsAppSend?.kind === 'single_invite'
            ? 'This will immediately send the guest a WhatsApp invitation.'
            : pendingWhatsAppSend?.invitePackLabel
              ? `This will immediately send a WhatsApp message to ${pendingWhatsAppSend?.recipientCount || 0} guest(s) using "${pendingWhatsAppSend.invitePackLabel}".`
              : `This will immediately send a WhatsApp message to ${pendingWhatsAppSend?.recipientCount || 0} guest(s).`}
          confirmLabel={pendingWhatsAppSend?.kind === 'reminder' ? 'Send reminder' : 'Send invitation'}
          disabled={sendingWhatsApp}
          testId="confirm-guest-whatsapp-send-btn"
        />

        <Dialog
          open={Boolean(giftDialogGuest)}
          onOpenChange={(open) => {
            if (!open) {
              setGiftDialogGuest(null);
              setReceivedGift(null);
              setGiftForm(EMPTY_RECEIVED_GIFT_FORM);
              setIsGiftEditing(false);
            }
          }}
        >
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-[#E7D9C7] bg-[#FFFCF7]">
            <DialogHeader>
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FFF0CB] text-[#96631D]">
                  <Gift className="h-5 w-5" weight="duotone" />
                </span>
                <div>
                  <DialogTitle>{eventTypeConfig.labels.giftTrackingTitle} — {giftDialogGuest?.name}</DialogTitle>
                  <DialogDescription className="mt-1">
                    Private host reference. Guests cannot see the gift, amount, notes, or thank-you draft.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {giftLoading ? (
              <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
                <SpinnerGap className="mr-2 h-4 w-4 animate-spin" /> Loading gift record…
              </div>
            ) : (
              <div className="space-y-5">
                {receivedGift && !isGiftEditing ? (
                  <div className="rounded-xl border border-[#E7D9C7] bg-white p-4" data-testid="gift-record-summary">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <Badge className="border-0 bg-[#FFF0CB] text-[#875A1B] hover:bg-[#FFF0CB]">
                          {receivedGift.kind === 'cash' ? 'Monetary gift' : 'Gift or item'}
                        </Badge>
                        <h3 className="mt-3 break-words font-serif text-xl font-semibold text-[#332A22]">
                          {receivedGift.description || (receivedGift.kind === 'cash' ? 'Monetary gift' : 'Gift recorded')}
                        </h3>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 border-[#DCCDBA]"
                        onClick={() => {
                          setGiftForm(giftToForm(receivedGift));
                          setIsGiftEditing(true);
                        }}
                        data-testid="edit-gift-record-btn"
                      >
                        <PencilSimple className="mr-1.5 h-4 w-4" /> Edit
                      </Button>
                    </div>

                    <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-lg bg-[#FAF7F2] px-3 py-2.5">
                        <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[#8B8177]">
                          {receivedGift.kind === 'cash' ? 'Amount' : 'Estimated value'}
                        </dt>
                        <dd className="mt-1 text-sm font-semibold text-[#332A22]">
                          {formatGiftAmount(receivedGift) || 'Not recorded'}
                        </dd>
                      </div>
                      <div className="rounded-lg bg-[#FAF7F2] px-3 py-2.5">
                        <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[#8B8177]">Received on</dt>
                        <dd className="mt-1 text-sm font-semibold text-[#332A22]">
                          {formatGiftReceivedDate(receivedGift.received_on) || 'Date not recorded'}
                        </dd>
                      </div>
                    </dl>

                    {receivedGift.notes && (
                      <div className="mt-3 rounded-lg border border-[#EEE5DA] bg-[#FFFCF8] px-3 py-2.5">
                        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[#8B8177]">Host notes</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-[#4B423A]">{receivedGift.notes}</p>
                      </div>
                    )}

                    <div className="mt-4 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={removeReceivedGift}
                        disabled={giftSaving || thankYouGenerating}
                      >
                        <Trash className="mr-1.5 h-4 w-4" /> Remove record
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-[#E7D9C7] bg-white p-4" data-testid="gift-record-form">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>What did the guest bring?</Label>
                        <Select
                          value={giftForm.kind}
                          onValueChange={(value) => {
                            setGiftForm((current) => ({ ...current, kind: value }));
                            if (value !== 'cash') setMentionGiftAmount(false);
                          }}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gift">A gift or item</SelectItem>
                            <SelectItem value="cash">A monetary gift</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gift-received-on">Received on</Label>
                        <Input id="gift-received-on" type="date" value={giftForm.received_on} onChange={(event) => setGiftForm((current) => ({ ...current, received_on: event.target.value }))} />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="gift-description">
                          {giftForm.kind === 'cash' ? 'Description (optional)' : 'Gift description'}
                        </Label>
                        <Input
                          id="gift-description"
                          value={giftForm.description}
                          onChange={(event) => setGiftForm((current) => ({ ...current, description: event.target.value }))}
                          placeholder={giftForm.kind === 'cash' ? 'e.g., Cash envelope' : eventTypeConfig.labels.giftDescriptionPlaceholder}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gift-amount">{giftForm.kind === 'cash' ? 'Amount' : 'Estimated value (optional)'}</Label>
                        <Input
                          id="gift-amount"
                          type="number"
                          min="0"
                          step="0.01"
                          value={giftForm.amount}
                          onChange={(event) => setGiftForm((current) => ({ ...current, amount: event.target.value }))}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select value={giftForm.currency} onValueChange={(value) => setGiftForm((current) => ({ ...current, currency: value }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['INR', 'USD', 'GBP', 'EUR', 'AED', 'AUD', 'CAD'].map((currency) => (
                              <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="gift-notes">Host notes (optional)</Label>
                        <Textarea
                          id="gift-notes"
                          rows={3}
                          value={giftForm.notes}
                          onChange={(event) => setGiftForm((current) => ({ ...current, notes: event.target.value }))}
                          placeholder="Add any detail that will help personalize the thank-you note."
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                      {receivedGift && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setGiftForm(giftToForm(receivedGift));
                            setIsGiftEditing(false);
                          }}
                          disabled={giftSaving || thankYouGenerating}
                        >
                          Cancel
                        </Button>
                      )}
                      <Button type="button" onClick={() => persistReceivedGift()} disabled={giftSaving || thankYouGenerating}>
                        {giftSaving ? <SpinnerGap className="mr-1.5 h-4 w-4 animate-spin" /> : <Gift className="mr-1.5 h-4 w-4" />}
                        {receivedGift ? 'Save changes' : 'Save gift'}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="rounded-xl border border-[#E3D6ED] bg-gradient-to-br from-[#FCF8FF] to-white p-4">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F0E5F7] text-[#76528C]">
                      <Sparkle className="h-4 w-4" weight="fill" />
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-[#342B3B]">Automated Thank-You Writer</h3>
                      <p className="mt-1 text-xs leading-5 text-[#776C7D]">Drafts a note addressed to this guest and tied to the saved gift without inventing personal memories.</p>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Tone</Label>
                      <Select value={thankYouTone} onValueChange={setThankYouTone}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="warm">Warm</SelectItem>
                          <SelectItem value="formal">Formal</SelectItem>
                          <SelectItem value="short">Short</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="thank-you-sender">Sign from</Label>
                      <Input
                        id="thank-you-sender"
                        value={thankYouSender}
                        onChange={(event) => setThankYouSender(event.target.value)}
                        placeholder={eventTypeConfig.labels.thankYouSenderPlaceholder}
                      />
                    </div>
                  </div>
                  {giftForm.kind === 'cash' && (
                    <label className="mt-3 flex items-center gap-2 text-xs text-[#685D6E]">
                      <Checkbox checked={mentionGiftAmount} onCheckedChange={(checked) => setMentionGiftAmount(checked === true)} />
                      Mention the exact amount in the draft
                    </label>
                  )}
                  <AIPremiumButton
                    featureName="Automated thank-you writing"
                    className="mt-4"
                    onClick={generateThankYouDraft}
                    disabled={giftSaving || thankYouGenerating}
                  >
                    {thankYouGenerating ? <><SpinnerGap className="animate-spin" /> Drafting note</> : <> Draft thank-you note</>}
                  </AIPremiumButton>

                  {receivedGift?.thank_you_draft && (
                    <div className="mt-4 rounded-lg border border-[#E3D6ED] bg-white p-3">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#806B8D]">Draft</span>
                        <Button type="button" variant="ghost" size="sm" onClick={copyThankYouDraft}>
                          <CopySimple className="mr-1.5 h-4 w-4" /> Copy
                        </Button>
                      </div>
                      <p className="whitespace-pre-wrap text-sm leading-6 text-[#413948]">{receivedGift.thank_you_draft}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Guest Profile Dialog */}
        <Dialog open={showGuestDialog} onOpenChange={setShowGuestDialog}>
          <DialogContent className="max-w-4xl border-[#E4D8CA] bg-[#FBF8F3] p-0 shadow-[0_24px_70px_rgba(47,42,38,0.22)]">
            {selectedGuest && (
              <>
                <DialogHeader className="border-b border-[#E8DED3] bg-white px-5 py-4 pr-12">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-[#E5D8C9] bg-[#F8F1E7] font-serif text-lg font-semibold text-[#60755F]">
                      {getGuestInitials(selectedGuest.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <DialogTitle className="truncate font-serif text-2xl text-[#24201C]">{selectedGuest.name}</DialogTitle>
                      <DialogDescription className="mt-1">Compact guest profile</DialogDescription>
                    </div>
                  </div>
                </DialogHeader>
                <div className="grid gap-4 p-5 lg:grid-cols-[1fr_340px]">
                  <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1.8fr)_repeat(3,minmax(0,1fr))]">
                      <div className="rounded-xl border border-[#E5D8C9] bg-white p-3">
                        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[#958A80]">RSVP</p>
                        {selectedGuest.assigned_itinerary_events?.length > 0 ? (
                          <div className="mt-2 space-y-1.5">
                            {selectedGuest.assigned_itinerary_events.map((item) => (
                              <div key={item.id} className="flex items-center justify-between gap-2">
                                <span className="min-w-0 truncate text-xs font-medium text-[#2F2A26]" title={item.name}>
                                  {item.name}
                                </span>
                                <span className="shrink-0">
                                  {getRSVPBadge(getGuestEventRsvpStatus(selectedGuest, item.id))}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-2">{getRSVPBadge(selectedGuest.rsvp_status)}</div>
                        )}
                      </div>
                      {eventTypeConfig.features.showGuestSides && <div className="rounded-xl border border-[#E5D8C9] bg-white p-3">
                        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[#958A80]">Side</p>
                        <div className="mt-2">{getGuestSideBadge(selectedGuest.guest_side)}</div>
                      </div>}
                      <div className="rounded-xl border border-[#E5D8C9] bg-white p-3">
                        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[#958A80]">WhatsApp</p>
                        <div className="mt-2">{getWhatsAppStatusBadge(selectedGuest)}</div>
                      </div>
                      {eventTypeConfig.features.showAccommodation && <div className="rounded-xl border border-[#E5D8C9] bg-white p-3">
                        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[#958A80]">Room</p>
                        <p className="mt-2 text-sm font-semibold text-[#2F2A26]">
                          {!guestRequiresRoom(selectedGuest)
                            ? 'Not required'
                            : getGuestRoomAllocationState(selectedGuest) === 'partial'
                            ? selectedGuest.room_allocation_label || 'Partially allocated'
                            : isGuestRoomAllocated(selectedGuest)
                            ? selectedGuest.room_allocation_label || 'Room assigned'
                            : selectedGuest.rsvp_status === 'declined'
                              ? 'Not required'
                              : selectedGuest.room_allocation_label || 'Not assigned'}
                        </p>
                      </div>}
                    </div>
                    <div className="rounded-2xl border border-[#E5D8C9] bg-white p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <UserCircle className="h-4 w-4 text-[#60755F]" />
                        <h3 className="text-sm font-semibold text-[#2F2A26]">Essentials</h3>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {renderDetailLine('Phone', selectedGuest.phone)}
                        {renderDetailLine('Email', selectedGuest.email)}
                        {renderDetailLine('Dietary', selectedGuest.dietary_restrictions || 'None specified')}
                        {renderDetailLine(
                          'Invite sent',
                          selectedGuest.whatsapp_sent_at
                            ? new Date(selectedGuest.whatsapp_sent_at).toLocaleString()
                            : 'Not sent yet'
                        )}
                      </div>
                    </div>
                    {hasAdminAccess && eventTypeConfig.features.showGuestGiftTracking && (
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-center justify-between gap-4 rounded-2xl border p-4 text-left transition-colors',
                          selectedGuest.has_received_gift
                            ? 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-300 hover:bg-emerald-50'
                            : 'border-[#E5D8C9] bg-white hover:border-[#CBA969] hover:bg-[#FFFCF5]'
                        )}
                        onClick={() => openReceivedGiftDialog(selectedGuest)}
                        data-gift-recorded={selectedGuest.has_received_gift ? 'true' : 'false'}
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <span className={cn(
                            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                            selectedGuest.has_received_gift
                              ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200'
                              : 'bg-slate-100 text-slate-500'
                          )}>
                            <Gift className="h-5 w-5" weight={selectedGuest.has_received_gift ? 'fill' : 'regular'} />
                          </span>
                          <span className="min-w-0">
                            <strong className="block text-sm text-[#2F2A26]">{eventTypeConfig.labels.giftTrackingTitle}</strong>
                            <small className="mt-1 block text-xs text-[#7A7168]">
                              {selectedGuest.has_received_gift ? 'Recorded privately for host reference' : 'Add an item or monetary gift'}
                            </small>
                          </span>
                        </span>
                        <ArrowRight className="h-4 w-4 shrink-0 text-[#8F8174]" />
                      </button>
                    )}
                    <div className="rounded-2xl border border-[#E5D8C9] bg-white p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <UsersThree className="h-4 w-4 text-[#60755F]" />
                          <h3 className="text-sm font-semibold text-[#2F2A26]">Guest Group</h3>
                        </div>
                        <Badge variant="outline" className="border-[#E5D8C9] bg-[#FBF8F3] text-[#7A5A28]">
                          {selectedGuest.party_summary?.total_party_size
                            ?? ((selectedGuest.phone_group?.length || 1) + getGuestGroupAccompanyingCount(selectedGuest.phone_group || [selectedGuest]))} people
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(selectedGuest.phone_group?.length ? selectedGuest.phone_group : [selectedGuest])
                          .slice(0, showFullGuestGroup ? undefined : 4)
                          .map((item) => (
                          <Badge
                            key={item.id}
                            variant="outline"
                            className={cn(
                              'max-w-[180px] truncate border-[#E5D8C9]',
                              item.id === selectedGuest.id
                                ? 'bg-[#60755F] text-white'
                                : 'bg-[#FBF8F3] text-[#5F574F]'
                            )}
                          >
                            {item.name}
                          </Badge>
                        ))}
                        {(selectedGuest.phone_group?.length || 1) > 4 && (
                          <button
                            type="button"
                            onClick={() => setShowFullGuestGroup((current) => !current)}
                            className="inline-flex h-6 items-center rounded-md border border-[#D8CBB9] bg-white px-2 text-xs font-semibold text-[#60755F] transition-colors hover:border-[#60755F] hover:bg-[#F4F8F2]"
                          >
                            {showFullGuestGroup ? 'Show less' : `+${(selectedGuest.phone_group?.length || 1) - 4} more`}
                          </button>
                        )}
                      </div>
                      {(selectedGuest.party_summary?.unnamed_accompanying_count || 0) > 0 && (
                        <p className="mt-2 text-xs text-[#7A7168]">
                          {selectedGuest.party_summary.unnamed_accompanying_count} unnamed companion{selectedGuest.party_summary.unnamed_accompanying_count === 1 ? '' : 's'} included
                        </p>
                      )}
                    </div>

                    <div className="hidden">
                    <div>
                      <Label className="text-muted-foreground">Contact Information</Label>
                      <p className="font-medium">{selectedGuest.email || 'No email'}</p>
                      <p className="text-sm text-muted-foreground">{selectedGuest.phone || 'No phone'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">RSVP Status</Label>
                      <div className="mt-1">{getRSVPBadge(selectedGuest.rsvp_status)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Guest Side</Label>
                      <div className="mt-1">{getGuestSideBadge(selectedGuest.guest_side)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">WhatsApp Invite Status</Label>
                      <div className="mt-1">{getWhatsAppStatusBadge(selectedGuest)}</div>
                      {selectedGuest.whatsapp_sent_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Sent: {new Date(selectedGuest.whatsapp_sent_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Dietary Restrictions</Label>
                      <p className="font-medium">{selectedGuest.dietary_restrictions || 'None specified'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Guest Group</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(selectedGuest.phone_group?.length ? selectedGuest.phone_group : [selectedGuest]).map((item) => (
                          <Badge key={item.id} variant={item.id === selectedGuest.id ? 'default' : 'outline'}>
                            {item.name}
                          </Badge>
                        ))}
                      </div>
                      {selectedGuest.companion_guests?.length > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Companions: {selectedGuest.companion_guests.map((item) => item.name).join(', ')}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Assigned Itinerary</Label>
                      {selectedGuest.assigned_itinerary_events?.length > 0 ? (
                        <div className="mt-2 space-y-2">
                          {selectedGuest.assigned_itinerary_events.map((item) => (
                            <div key={item.id} className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                              <p className="font-medium">{item.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {[item.date ? new Date(item.date).toLocaleDateString() : '', item.start_time, item.venue_name].filter(Boolean).join(' - ')}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-muted-foreground">No itinerary events assigned</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Room Assignment</Label>
                      <p className="font-medium">
                        {!guestRequiresRoom(selectedGuest)
                          ? 'Not required'
                          : getGuestRoomAllocationState(selectedGuest) === 'partial'
                          ? selectedGuest.room_allocation_label || 'Partially allocated'
                          : isGuestRoomAllocated(selectedGuest)
                          ? selectedGuest.room_allocation_label || 'Room assigned'
                          : selectedGuest.rsvp_status === 'declined'
                            ? 'Not required - RSVP declined'
                            : selectedGuest.room_allocation_label || 'Not assigned'}
                      </p>
                    </div>
                    <div>
                      {selectedGuest.id_info?.id_type || selectedGuest.id_info?.id_uploads?.length ? (
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">ID Information</Label>
                          {selectedGuest.id_info?.id_uploads?.length > 0 ? (
                            <div className="space-y-2">
                              {getIdUploads(selectedGuest.id_info).map((upload) => (
                                <button
                                  key={upload.key}
                                  type="button"
                                  onClick={() => openIdPreview(upload.url, upload.id_type || 'ID Proof')}
                                  className="w-full flex items-center gap-3 rounded-lg border p-3 bg-muted/30 hover:text-wedding-sage transition-colors text-left"
                                >
                                  <Envelope className="w-4 h-4 text-wedding-sage" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">
                                      {upload.id_type || 'ID Proof'}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Not provided</p>
                      )}
                    </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-[#E5D8C9] bg-white p-4">
                      <div className="mb-3 flex items-center gap-2">
                        <AirplaneLanding className="h-4 w-4 text-[#60755F]" />
                        <h3 className="text-sm font-semibold text-[#2F2A26]">Travel</h3>
                      </div>
                      <div className="space-y-3">
                        {renderTravelCard(selectedGuest, 'arrival')}
                        {renderTravelCard(selectedGuest, 'departure')}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-[#E5D8C9] bg-white p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <IdentificationCard className="h-4 w-4 text-[#60755F]" />
                          <h3 className="text-sm font-semibold text-[#2F2A26]">Documents</h3>
                        </div>
                        {hasAdminAccess && (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 border-[#DED2C4] bg-white px-2.5 text-xs"
                              onClick={() => document.getElementById(`id-proof-upload-${selectedGuest.id}`)?.click()}
                              disabled={uploadingGuestId}
                            >
                              <Upload className="mr-1.5 h-3.5 w-3.5" />
                              {uploadingGuestId ? 'Uploading' : 'Upload ID'}
                            </Button>
                            <input
                              id={`id-proof-upload-${selectedGuest.id}`}
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.webp"
                              className="hidden"
                              onChange={(event) => handleUploadGuestIdProof(event, selectedGuest)}
                            />
                          </>
                        )}
                      </div>
                      {selectedGuest.id_info?.id_type || selectedGuest.id_info?.id_uploads?.length ? (
                        getIdUploads(selectedGuest.id_info).length > 0 ? (
                          <div className="space-y-2">
                            {getIdUploads(selectedGuest.id_info).map((upload) => (
                              <button
                                key={upload.key}
                                type="button"
                                onClick={() => openIdPreview(upload.url, upload.id_type || 'ID Proof')}
                                className="flex w-full items-center gap-3 rounded-xl border border-[#EDE4DA] bg-[#FBF8F3] px-3 py-2 text-left transition-colors hover:border-[#CDBEA9] hover:text-[#60755F]"
                              >
                                <Envelope className="h-4 w-4 shrink-0 text-[#60755F]" />
                                <span className="truncate text-sm font-medium">{upload.id_type || 'ID Proof'}</span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#7A7168]">{selectedGuest.id_info?.id_type || 'Not provided'}</p>
                        )
                      ) : (
                        <p className="text-sm text-[#7A7168]">ID proof not provided</p>
                      )}
                    </div>
                  </div>
                  <div className="hidden">
                    {/* Arrival Section */}
                    <div className="border rounded-lg p-3 bg-green-50/50">
                      <div className="flex items-center justify-between gap-3">
                        <Label className="text-green-700 font-medium">Arrival Details</Label>
                        {hasAdminAccess && (
                          <>
                            <AIPremiumButton
                              featureName="ticket reading"
                              type="button"
                              variant="outline"
                              size="sm"
                              onManualEntry={() => setManualTravelEntry({ guest: selectedGuest, ticketType: 'arrival' })}
                            >
                              Upload Ticket
                            </AIPremiumButton>
                          </>
                        )}
                      </div>
                      {selectedGuest.arrival_info?.arrival_date ? (
                        <div className="text-sm mt-1">
                          <p className="capitalize font-medium">{selectedGuest.arrival_info.arrival_mode} on {new Date(selectedGuest.arrival_info.arrival_date).toLocaleDateString()}</p>
                          {selectedGuest.arrival_info.arrival_time && <p className="text-muted-foreground">Time: {selectedGuest.arrival_info.arrival_time}</p>}
                          {selectedGuest.arrival_info.arrival_details && <p className="text-muted-foreground">{selectedGuest.arrival_info.arrival_details}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground mt-1">Not provided</p>
                      )}
                      
                      {/* Arrival Tickets */}
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <p className="text-xs text-green-700">
                          {selectedGuest.arrival_info?.arrival_tickets?.length || 0} ticket(s) uploaded
                        </p>
                        
                        {/* Passengers from arrival tickets */}
                        {(() => {
                          const arrivalPassengers = new Set();
                          (selectedGuest.arrival_info?.arrival_tickets || []).forEach(ticket => {
                            ticket.extracted_data?.passenger_names?.forEach(name => arrivalPassengers.add(name));
                          });
                          const passengerList = Array.from(arrivalPassengers);
                          
                          if (passengerList.length > 0) {
                            return (
                              <div className="mt-1">
                                <span className="text-xs text-muted-foreground">Passengers: </span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {passengerList.map((name, idx) => (
                                    <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                                      {name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        {/* Accompanying guests from arrival tickets */}
                        {(() => {
                          const accompanyingIds = new Set();
                          (selectedGuest.arrival_info?.arrival_tickets || []).forEach(ticket => {
                            ticket.accompanying_guest_ids?.forEach(id => accompanyingIds.add(id));
                          });
                          const accompanyingList = Array.from(accompanyingIds);
                          
                          if (accompanyingList.length > 0) {
                            return (
                              <div className="mt-1">
                                <span className="text-xs text-muted-foreground">Accompanying: </span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {accompanyingList.map((guestId) => {
                                    const guest = guests.find(g => g.id === guestId);
                                    return (
                                      <span key={guestId} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-200 text-green-900 font-medium">
                                        {guest?.name || guestId}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                    
                    {/* Departure Section */}
                    <div className="border rounded-lg p-3 bg-red-50/50">
                      <div className="flex items-center justify-between gap-3">
                        <Label className="text-red-700 font-medium">Departure Details</Label>
                        {hasAdminAccess && (
                          <>
                            <AIPremiumButton
                              featureName="ticket reading"
                              type="button"
                              variant="outline"
                              size="sm"
                              onManualEntry={() => setManualTravelEntry({ guest: selectedGuest, ticketType: 'departure' })}
                            >
                              Upload Ticket
                            </AIPremiumButton>
                          </>
                        )}
                      </div>
                      {selectedGuest.arrival_info?.departure_date ? (
                        <div className="text-sm mt-1">
                          <p className="capitalize font-medium">{selectedGuest.arrival_info.departure_mode} on {new Date(selectedGuest.arrival_info.departure_date).toLocaleDateString()}</p>
                          {selectedGuest.arrival_info.departure_time && <p className="text-muted-foreground">Time: {selectedGuest.arrival_info.departure_time}</p>}
                          {selectedGuest.arrival_info.departure_details && <p className="text-muted-foreground">{selectedGuest.arrival_info.departure_details}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground mt-1">Not provided</p>
                      )}
                      
                      {/* Departure Tickets */}
                      <div className="mt-2 pt-2 border-t border-red-200">
                        <p className="text-xs text-red-700">
                          {selectedGuest.arrival_info?.departure_tickets?.length || 0} ticket(s) uploaded
                        </p>
                        
                        {/* Passengers from departure tickets */}
                        {(() => {
                          const departurePassengers = new Set();
                          (selectedGuest.arrival_info?.departure_tickets || []).forEach(ticket => {
                            ticket.extracted_data?.passenger_names?.forEach(name => departurePassengers.add(name));
                          });
                          const passengerList = Array.from(departurePassengers);
                          
                          if (passengerList.length > 0) {
                            return (
                              <div className="mt-1">
                                <span className="text-xs text-muted-foreground">Passengers: </span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {passengerList.map((name, idx) => (
                                    <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                                      {name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        {/* Accompanying guests from departure tickets */}
                        {(() => {
                          const accompanyingIds = new Set();
                          (selectedGuest.arrival_info?.departure_tickets || []).forEach(ticket => {
                            ticket.accompanying_guest_ids?.forEach(id => accompanyingIds.add(id));
                          });
                          const accompanyingList = Array.from(accompanyingIds);
                          
                          if (accompanyingList.length > 0) {
                            return (
                              <div className="mt-1">
                                <span className="text-xs text-muted-foreground">Accompanying: </span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {accompanyingList.map((guestId) => {
                                    const guest = guests.find(g => g.id === guestId);
                                    return (
                                      <span key={guestId} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-200 text-red-900 font-medium">
                                        {guest?.name || guestId}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                {selectedGuest.phone && !selectedGuest.user_id && (
                  <DialogFooter>
                    <Button
                      onClick={() => setPendingWhatsAppSend({
                        kind: 'single_invite',
                        guestId: selectedGuest.id,
                        guestName: selectedGuest.name,
                        recipientCount: 1,
                        closeGuestDialog: true,
                      })}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <WhatsappLogo className="w-4 h-4 mr-2" weight="fill" />
                      Send WhatsApp Invite
                    </Button>
                  </DialogFooter>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Guest Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Guest Group</DialogTitle>
              <DialogDescription>Update shared details and the names using this phone number</DialogDescription>
            </DialogHeader>
            {editingGuest && (
              <form onSubmit={handleSaveEdit} className="space-y-4">
                {renderGuestGroupFields(editingGuest, setEditingGuest, 'edit')}
                {renderGuestNameRows(editingGuest, setEditingGuest, 'edit')}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90" data-testid="update-guest-btn">
                    Save
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <ManualTravelDetailsDialog
          open={Boolean(manualTravelEntry)}
          onOpenChange={(open) => { if (!open) setManualTravelEntry(null); }}
          eventId={currentEvent?.id}
          guest={manualTravelEntry?.guest}
          ticketType={manualTravelEntry?.ticketType || 'arrival'}
          onSaved={() => refreshGuestAfterManualTravel(manualTravelEntry?.guest?.id)}
        />

        <Dialog open={!!idPreviewDialog} onOpenChange={() => setIdPreviewDialog(null)}>
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>{idPreviewDialog?.label || 'ID Preview'}</DialogTitle>
            </DialogHeader>
            {idPreviewDialog?.url && (
              <div className="py-2">
                {isPdfFile(idPreviewDialog.url) ? (
                  <iframe
                    src={idPreviewDialog.url}
                    title={idPreviewDialog.label || 'ID Preview'}
                    className="w-full h-[70vh] rounded border"
                  />
                ) : (
                  <img
                    src={idPreviewDialog.url}
                    alt={idPreviewDialog.label || 'ID Preview'}
                    className="w-full max-h-[70vh] object-contain rounded border"
                  />
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
}
